<script setup lang="ts">
import { ref, computed, watch, nextTick, onMounted, onUnmounted } from 'vue';
import { useRouter } from 'vue-router';
import { usePresetProgress } from '@/features/startTask/preset_task/shared/preset/composables/usePresetProgress';
import type { PreSetSelection } from '@/features/startTask/preset_task/shared/preset/types/preset.types';
import type { OfficerDetail, OfficerRoleType } from '@/features/AI-documents/types/docGen.types';
import {
  buildIncorporationPostVariables,
  detectIncorporationPostDocFlags,
} from '../mappers/incorporationPostMapper';
import type {
  IncorporationPostInput,
  DirectorInput,
  CosecInput,
  AllotteeInput,
} from '../mappers/incorporationPostMapper';
import { getTaskConfigByCode } from '@/features/startTask/api/taskConfigApi';
import { docGenApi } from '@/features/AI-documents/api/docGenApi';
import StructuredAddressInput from './StructuredAddressInput.vue';
import msicCodes from '../../components/shared/data/common/msic-codes.json';
import { excludeCrsUnfilableMsic } from '@/shared/constants/crsMsic';
import { useDateField, formatDateByUserFormat } from '@/shared/composables/dateUtils';
import { fetchEntityAddresses } from '@/shared/composables/useEntityAddresses';

interface PersonLookupContext {
  fieldGroup:
    | 'director'
    | 'cosec'
    | 'allottee'
    | 'auditor'
    | 'auditorAddress'
    | 'taxAgent'
    | 'taxAgentAddress'
    | 'lodger'
    | 'nomineeDirector'
    | 'lhdnDirector'
    | 'cosecFirm';
  index?: number;
  defaultRole: OfficerRoleType | null;
}

const props = defineProps<{
  selection: PreSetSelection;
  entityData: Record<string, any> | null;
  taskTitle: string;
  isReady: boolean;
  currentStep: string;
  selectedPerson?: OfficerDetail | null;
  activePersonContext?: PersonLookupContext | null;
  selectedAddress?: { value: string; type: string; position?: string } | null;
  activeAddressContext?: { fieldGroup: string; index: number } | null;
  /** Edit 모드 — 값이 있으면 기존 task 수정(Update). 호스트가 restoreState 로 입력 복원. */
  editTaskId?: number | null;
}>();

// Edit(수정) 모드 — 버튼 라벨 'Update Task' + entity 자동 prefill skip(restoreState 가 채움) + create→update 분기.
const editMode = computed(() => !!props.editTaskId);

// restoreState 직후 입력 스냅샷(기준선). Update Task 는 실제 변경이 있을 때만 활성화.
const editBaseline = ref<string | null>(null);
const isDirty = computed(() => {
  if (!editMode.value) return true; // Create 모드는 항상 활성
  if (editBaseline.value == null) return false; // 기준선 캡처 전 — 비활성
  try { return JSON.stringify(gatherState()) !== editBaseline.value; } catch { return true; }
});

const emit = defineEmits<{
  (e: 'back'): void;
  (e: 'task-created', data: { taskId: number; taskTitle: string }): void;
  (e: 'request-person-lookup', ctx: PersonLookupContext): void;
  (e: 'request-address-lookup', ctx: { fieldGroup: string; index: number; addresses: any[]; subjectLabel?: string; directInput?: boolean }): void;
  (e: 'change-entity'): void;
  (e: 'change-task-title'): void;
}>();

const router = useRouter();

// ─── 문서 분기 ───
const docFlags = computed(() => detectIncorporationPostDocFlags(props.selection.documents));

// ─── 회사 정보 (entity 선택 시 prefill) ───
const companyName = ref('');
const companyRegistrationNo = ref('');
const registeredOfficeAddress = ref('');
const businessAddress = ref('');
const companyTel = ref('');
const incorporationDateField = useDateField();
const incorporationDate = incorporationDateField.dateValue;
const incorporationDateMenu = incorporationDateField.dateMenu;
const incorporationDatePickerValue = incorporationDateField.datePickerValue;
const incorporationDatePickerProps = incorporationDateField.datePickerProps;
const incorporationLocalText = incorporationDateField.localText;
const incorporationOnBlur = incorporationDateField.onDateTextBlur;
const incorporationDatePlaceholder = incorporationDateField.datePlaceholder;
const entityCd = computed(() => props.entityData?.entityCd || props.entityData?.icpCd || '');
/** L_ENTITY_PROFILE 5개 전용 주소 컬럼 목록 — Address History 버튼용. */
const entityAddresses = ref<any[]>([]);

/** Entity prefill loading 상태 — chat 에서 entity 선택 후 모든 자동 채움 완료 전 폼 가림 + 로딩바 표시. */
const entityLoading = ref(false);
/** 폼 섹션 노출 조건 — Task Title + Entity 둘 다 선택 완료 + prefill 끝났을 때. */
const formReady = computed(() => !!props.taskTitle && !!entityCd.value && !entityLoading.value);

// ─── Entity 변경 시 자동 prefill ───
//   1) entityData (chat 에서 받은 기본 정보) 즉시 반영
//   2) fetchEntityAddresses — L_ENTITY_PROFILE 5개 전용 컬럼 기반 주소 일괄 조회
//   3) /api/entity_my/profile — incorporationDate / FYE / 회사 전화번호 / articleNoOptions
//   4) Directors / Secretaries / Members 자동 바인딩
watch(
  () => entityCd.value,
  async (cd) => {
    if (!cd) {
      entityLoading.value = false;
      return;
    }
    // Edit 모드: 저장된 입력을 restoreState 가 채우므로 entity 자동 prefill(이사/비서/멤버 덮어쓰기) skip.
    //   단 콤보박스 옵션(Constitution Clause = articleNoOptions)은 entity profile 에서 로드되므로,
    //   필드는 건드리지 않고 "옵션만" 채워 콤보박스가 정상 동작하도록 한다.
    if (editMode.value) {
      try {
        const optsJson = await fetch(`/api/entity_my/profile/resolution-options`, { credentials: 'include' })
          .then((r) => r.json())
          .catch(() => null);
        const fastOpts = Array.isArray(optsJson?.articleNoOptions) ? optsJson.articleNoOptions : [];
        if (fastOpts.length) {
          entityArticleNoOptions.value = Array.from(
            new Set(fastOpts.map((o: any) => String(o ?? '').trim()).filter(Boolean)),
          );
        }
      } catch { /* 옵션 로딩 실패는 무시 — 콤보박스는 자유 입력으로 동작 */ }
      entityLoading.value = false;
      return;
    }
    entityLoading.value = true;
    try {
    // 1) 즉시 적용 — entityData 가 제공하는 기본값
    if (props.entityData?.companyName || props.entityData?.entityName) {
      companyName.value = props.entityData.companyName || props.entityData.entityName || '';
    }
    if (props.entityData?.registrationNo) {
      companyRegistrationNo.value = props.entityData.registrationNo;
    }

    // 2) Entity 주소 일괄 조회 (L_ENTITY_PROFILE 5개 전용 컬럼)
    const entityAddr = await fetchEntityAddresses(cd);
    if (entityAddr) {
      if (!companyName.value && entityAddr.companyName) companyName.value = entityAddr.companyName;
      if (!companyRegistrationNo.value && entityAddr.regNo) companyRegistrationNo.value = entityAddr.regNo;
      if (!registeredOfficeAddress.value && entityAddr.registered) {
        registeredOfficeAddress.value = entityAddr.registered;
      }
      if (!businessAddress.value && entityAddr.business) {
        businessAddress.value = entityAddr.business;
      }
      // Address History 버튼용 — 비어있지 않은 주소만 저장
      entityAddresses.value = entityAddr.addresses || [];
      // clauseNo (= L_ENTITY.ARTICLE_NO) 자동 prefill
      if (entityAddr.clauseNo && !constitutionClauseNo.value) {
        const parsed = parseClauseNo(entityAddr.clauseNo);
        if (entityArticleNoOptions.value.includes(parsed)) {
          constitutionClauseNo.value = parsed;
        }
      }
    }

    // 3) Entity profile (heavy: incorporationDate / FYE / tel / 주소) +
    //    articleNoOptions 는 전용 경량 엔드포인트(메모리 캐시)에서 병렬로 — 옵션 로딩 지연 제거.
    try {
      const [res, optsJson] = await Promise.all([
        fetch(`/api/entity_my/profile/${encodeURIComponent(cd)}`, { credentials: 'include' }),
        fetch(`/api/entity_my/profile/resolution-options`, { credentials: 'include' })
          .then((r) => r.json())
          .catch(() => null),
      ]);
      // articleNoOptions — 전용 엔드포인트 우선 (캐시 즉답).
      const fastOpts = Array.isArray(optsJson?.articleNoOptions) ? optsJson.articleNoOptions : [];
      if (fastOpts.length) {
        entityArticleNoOptions.value = Array.from(new Set(fastOpts.map((o: any) => String(o ?? '').trim()).filter(Boolean)));
      }
      if (res.ok) {
        const json = await res.json();
        const profile = json?.data || json?.profile || json || {};
        const accounting = profile?.accountingDates || {};
        const incDate = accounting?.incorporationDate || profile?.incorporationDate || '';
        if (incDate && !incorporationDate.value) {
          const iso = normalizeProfileDateToIso(incDate);
          if (iso) incorporationDate.value = iso;
        }
        const fye =
          accounting?.financialYearEnd ||
          accounting?.fiscalYearEnd ||
          profile?.financialYearEnd ||
          profile?.fiscalYearEnd ||
          profile?.FINANCIAL_YEAR_END ||
          '';
        if (fye && !financialYearEnd.value) {
          const iso = normalizeProfileDateToIso(fye);
          if (iso) financialYearEnd.value = iso;
        }
        const tel =
          profile?.companyTel || profile?.companyPhone || profile?.tel || profile?.mainContacts?.[0]?.phone || '';
        if (tel && !companyTel.value) companyTel.value = String(tel).trim();
        // Business 주소 fallback (entityAddr 에서 못 가져온 경우)
        if (!businessAddress.value) {
          const alt = profile?.alternateAddress;
          if (alt) {
            const altComposed =
              typeof alt === 'string' ? alt : alt?.composed || alt?.composedAddress || '';
            if (altComposed) businessAddress.value = altComposed;
          }
        }
        // Constitution articleNoOptions — 전용 엔드포인트가 비었을 때만 heavy profile 에서 fallback.
        if (!entityArticleNoOptions.value.length) {
          const articleOpts =
            profile?.resolution?.articleNoOptions ||
            profile?.data?.resolution?.articleNoOptions ||
            [];
          entityArticleNoOptions.value = Array.isArray(articleOpts)
            ? Array.from(new Set(articleOpts.map((o: any) => String(o ?? '').trim()).filter(Boolean)))
            : [];
        }
      }
    } catch (err) {
      console.warn('[IncorporationPost] entity profile failed:', err);
    }

    // 4) Directors / Secretaries / Members 자동 바인딩 (사용자 수정 보호 안 함 — entity 단위 덮어쓰기)
    await autoBindOfficers(cd);

    // 5) cosec 의 Firm Name / Firm Address 가 비어있으면 entity 정보로 prefill.
    //   (Petty Cash / Members WR 의 수신처 — 보통 비서 사무소 = 회사와 같은 주소 사용 케이스 다수)
    applyEntityDefaultsToCosecs();
    } finally {
      entityLoading.value = false;
    }
  },
  { immediate: true },
);

function applyEntityDefaultsToCosecs() {
  // Auto-populate removed — user selects Co-Sec firm via AI contact lookup or manual input.
}

/**
 * 주소 후보 배열에서 residential 우선, 없으면 첫 valid entry.
 *   백엔드 응답의 type 은 'residential' / 'service' / 'business' (소문자) — `listContactAddresses` 컨벤션.
 *   { value, idx } 반환 — chip highlight 용 index 추적.
 */
function pickDefaultAddress(addresses: any[]): { value: string; idx: number } {
  if (!Array.isArray(addresses) || !addresses.length) return { value: '', idx: -1 };
  const norm = (t: any) => String(t || '').trim().toLowerCase();
  const resIdx = addresses.findIndex((a) => norm(a?.type) === 'residential' && a?.value);
  if (resIdx >= 0) return { value: String(addresses[resIdx].value), idx: resIdx };
  const bizIdx = addresses.findIndex((a) => norm(a?.type) === 'business' && a?.value);
  if (bizIdx >= 0) return { value: String(addresses[bizIdx].value), idx: bizIdx };
  const firstIdx = addresses.findIndex((a) => a?.value);
  return firstIdx >= 0
    ? { value: String(addresses[firstIdx].value), idx: firstIdx }
    : { value: '', idx: -1 };
}

async function autoBindOfficers(cd: string) {
  // Directors
  try {
    const dirRes: any = await docGenApi.getDirectors(cd);
    const dirs = dirRes?.data?.directors || dirRes?.data || [];
    if (Array.isArray(dirs) && dirs.length) {
      directors.value = dirs.map((d: any): DirectorInput => {
        const addrs = Array.isArray(d.addresses) ? d.addresses : [];
        // primaryAddress 가 있으면 그것이 단일 주소 — 그대로 사용.
        // 없으면 addresses[] 에서 residential 우선 선택.
        let addr = d.primaryAddress || d.address || '';
        let selectedIdx = -1;
        if (!addr) {
          const picked = pickDefaultAddress(addrs);
          addr = picked.value;
          selectedIdx = picked.idx;
        } else {
          // primaryAddress 와 value 일치하는 첫 entry 의 index — chip highlight 용
          selectedIdx = addrs.findIndex((a: any) => a?.value === addr);
        }
        return {
          contactCd: d.contactCd || d.contactCode || '',
          name: d.name || d.contactName || '',
          email: d.contactEmail || '',
          address: addr,
          addresses: addrs,
          addressSelectedIdx: selectedIdx,
          isNominee: !!d.isNominee,
        };
      });
    }
  } catch (err) {
    console.warn('[IncorporationPost] getDirectors failed:', err);
  }

  // Secretaries → cosecs prefill (이미 비서가 entity 에 있으면 그걸 채움)
  try {
    const secRes: any = await docGenApi.getSecretaries(cd);
    const secs = secRes?.data?.secretaries || secRes?.data || [];
    if (Array.isArray(secs) && secs.length) {
      cosecs.value = secs.map((s: any): CosecInput => {
        const addrs = Array.isArray(s.addresses) ? s.addresses : [];
        let addr = s.primaryAddress || s.address || '';
        let selectedIdx = -1;
        if (!addr) {
          const picked = pickDefaultAddress(addrs);
          addr = picked.value;
          selectedIdx = picked.idx;
        } else {
          selectedIdx = addrs.findIndex((a: any) => a?.value === addr);
        }
        return {
          contactCd: s.contactCd || s.contactCode || '',
          name: s.name || s.contactName || '',
          licenseNo: s.licenseNumber || '',
          nricNo: s.contactRegNo || '',
          // firm = 비서가 소속된 회사명(corporate secretary firm) = L_OFFICER.FIRM_NO. businessOccupation(직책)과 혼동 금지.
          firm: s.firm || '',
          tel: s.mobileNo || '',
          email: s.contactEmail || '',
          fax: '',
          ssmCertNo: s.ssmPractisingCertificateNumber || '',
          companyName: '',
          address: addr,
          addresses: addrs,
          addressSelectedIdx: selectedIdx,
        };
      });
    } else if (docFlags.value.hasDcrAppointCosec || docFlags.value.hasSection236_3 || docFlags.value.hasPettyCash || docFlags.value.hasMembersWrConstitution) {
      // Co-Sec 관련 문서가 포함됐는데 DB에 비서가 없으면 빈 row 1개 — 사용자에게 입력 필요함을 알림.
      cosecs.value = [{ contactCd: '', name: '', licenseNo: '', nricNo: '', firm: '', tel: '', email: '', fax: '', ssmCertNo: '', companyName: '', address: '', addresses: [], addressSelectedIdx: -1 }];
    }
  } catch (err) {
    console.warn('[IncorporationPost] getSecretaries failed:', err);
  }

  // Members (= shareholders) → allottees prefill.
  //   Panel 의 Shares 칸 = 회사 전체 최종 보유 수량 (기존 + 이번 task 추가).
  //   prefill = 현재 entity 의 기존 보유 SUM. 사용자가 그대로 두면 delta=0 → skip,
  //   증가시키면 backend 가 delta 만 신규 allot INSERT.
  //   contactCd 기준 dedupe + shares SUM 으로 panel 한 줄 = DB SUM = mapper 합산값.
  try {
    const memRes: any = await docGenApi.getMembers(cd);
    const mems = memRes?.data?.members || memRes?.data?.shares || memRes?.data || [];
    if (Array.isArray(mems) && mems.length) {
      const dedupeMap = new Map<string, AllotteeInput>();
      for (const m of mems) {
        const cc = String(m.contactCd || '').trim();
        const nm = String(m.name || m.contactName || '').trim();
        if (!nm) continue;
        const key = cc || `name:${nm.toUpperCase()}`;
        const sh = Number(m.numberOfShares || m.shares || 0) || 0;
        const existing = dedupeMap.get(key);
        if (existing) {
          existing.shares += sh;
        } else {
          dedupeMap.set(key, { contactCd: cc, name: nm, shares: sh });
        }
      }
      allottees.value = Array.from(dedupeMap.values());
      // Total Shares 자동 합산 (회사 전체 issued shares)
      const total = allottees.value.reduce((sum, a) => sum + (Number(a.shares) || 0), 0);
      if (total > 0 && !totalShares.value) totalShares.value = String(total);
    }
  } catch (err) {
    console.warn('[IncorporationPost] getMembers failed:', err);
  }

  // Auditor 자동 매핑 — 첫 번째 감사인의 이름 + 주소 prefill
  // list endpoint 가 이제 addresses (branch + profile) 를 포함하므로 별도 detail 호출 불필요
  try {
    const audRes: any = await docGenApi.getAuditors(cd);
    const auds = audRes?.data?.auditors || [];
    if (Array.isArray(auds) && auds.length > 0) {
      const first = auds[0];
      if (first.name && !auditorName.value) auditorName.value = String(first.name).trim();
      // 기존 entity auditor 의 contactCd 도 잡아둬야 레지스터 시 신규 contact 재생성 방지.
      if (first.contactCd && !auditorContactCd.value) {
        auditorContactCd.value = String(first.contactCd).trim();
      }
      const addrs = Array.isArray(first.addresses) ? first.addresses : [];
      auditorAddresses.value = addrs;
      const picked = pickDefaultAddress(addrs);
      const addr = first.primaryAddress || picked.value || (addrs[0]?.value) || '';
      if (addr && !auditorAddress.value) {
        auditorAddress.value = addr;
        auditorAddressSelectedIdx.value = addrs.findIndex((a: any) => a?.value === addr);
      }
    }
  } catch (err) {
    console.warn('[IncorporationPost] getAuditors failed:', err);
  }
}

// ─── 이사 (다수 vs 단독 자동) ───
const directors = ref<DirectorInput[]>([]);
const isSoleDirectorOverride = ref<'auto' | 'sole' | 'multi'>('auto');
const isSoleDirector = computed(() => {
  if (isSoleDirectorOverride.value === 'sole') return true;
  if (isSoleDirectorOverride.value === 'multi') return false;
  return directors.value.filter((d) => d?.name?.trim()).length <= 1;
});

// ─── 비서 (다명) ───
const cosecs = ref<CosecInput[]>([]);
/** Co-Sec Firm Name — all secretaries share one firm (Petty Cash / Members WR / Section 236(3)).
 *   Master: 이 값 변경 시 모든 cosecs[i].firm 으로 자동 동기화 (행별 수정도 가능, master 가 다시 바뀌면 일괄 덮어쓰기). */
const cosecFirmName = ref('');
/** Co-Sec Firm Address — all secretaries share one firm address */
const cosecFirmAddress = ref('');

// Co-Sec Firm Name → 모든 비서 row 의 firm 자동 동기화.
//   - 마스터(cosecFirmName) 변경 → 모든 cosecs[i].firm 갱신 (사용자가 행별로 수정한 값도 덮어씀 — 의도된 동작)
//   - cosecs 배열 자체가 새로 채워질 때(autoBindOfficers 직후)도 trigger 되도록 cosecs.length 도 deps 에 포함
watch(
  () => [cosecFirmName.value, cosecs.value.length],
  () => {
    const name = String(cosecFirmName.value || '').trim();
    if (!name) return; // 마스터가 비어있으면 sync 안 함 (autoBind 값 유지)
    cosecs.value.forEach((c) => { c.firm = name; });
  },
);

// ─── 회계 ───
const financialYearEndField = useDateField();
const financialYearEnd = financialYearEndField.dateValue;
const financialYearEndMenu = financialYearEndField.dateMenu;
const financialYearEndPickerValue = financialYearEndField.datePickerValue;
const financialYearEndPickerProps = financialYearEndField.datePickerProps;
const financialYearEndLocalText = financialYearEndField.localText;
const financialYearEndOnBlur = financialYearEndField.onDateTextBlur;
const financialYearEndPlaceholder = financialYearEndField.datePlaceholder;
// FYE 가 비어 있으면 "해당 연도(인코퍼레이션 연도, 없으면 올해) 마지막 날" 을 사용자 날짜 포맷으로 제안 표시.
//   값이 들어오면 일반 placeholder(DD-MM-YYYY 등) 로 복귀.
const fyeSuggestedPlaceholder = computed(() => {
  if (financialYearEnd.value) return financialYearEndPlaceholder.value;
  let year = new Date().getFullYear();
  const inc = String(incorporationDate.value || '').trim();
  const m = inc.match(/(\d{4})/);
  if (m) year = parseInt(m[1], 10);
  try {
    return formatDateByUserFormat(new Date(year, 11, 31));
  } catch {
    return `31-12-${year}`;
  }
});
const firstAccountFrom = ref('');
const firstAccountTo = ref('');

// ─── 주식 / Allottee ───
const allottees = ref<AllotteeInput[]>([]);
const totalShares = ref<string>('');
const sharePrice = ref<string>('1.00');
const totalCashAmount = ref<string>('');
const totalCashAmountWords = ref<string>('');

/** 정수 → 영문 (Million / Thousand / Hundred / ...). 최대 9자리. */
function numberToEnglishWords(num: number): string {
  if (num === 0) return 'Zero';
  const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine',
    'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
  const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
  function chunk(n: number): string {
    if (n === 0) return '';
    if (n < 20) return ones[n];
    if (n < 100) return tens[Math.floor(n / 10)] + (n % 10 ? ' ' + ones[n % 10] : '');
    return ones[Math.floor(n / 100)] + ' Hundred' + (n % 100 ? ' ' + chunk(n % 100) : '');
  }
  const parts: string[] = [];
  const billion = Math.floor(num / 1_000_000_000);
  const million = Math.floor((num % 1_000_000_000) / 1_000_000);
  const thousand = Math.floor((num % 1_000_000) / 1_000);
  const remainder = num % 1_000;
  if (billion) parts.push(chunk(billion) + ' Billion');
  if (million) parts.push(chunk(million) + ' Million');
  if (thousand) parts.push(chunk(thousand) + ' Thousand');
  if (remainder) parts.push(chunk(remainder));
  return parts.join(' ');
}

/** RM 금액 → "Ringgit Malaysia X Only" or with sen. */
function ringgitToWords(amount: number): string {
  if (!isFinite(amount) || amount < 0) return '';
  const ringgit = Math.floor(amount);
  const sen = Math.round((amount - ringgit) * 100);
  const rWords = numberToEnglishWords(ringgit);
  if (sen === 0) return `Ringgit Malaysia ${rWords} Only`;
  return `Ringgit Malaysia ${rWords} and ${numberToEnglishWords(sen)} Sen Only`;
}

// ── 자동 계산 — allottees / sharePrice 변경 시 Total Shares / Total Cash / Amount in Words 동기화 ──
//   사용자가 직접 override 한 값은 다음 계산에서 다시 덮어쓰여짐 (auto-sync 우선).
watch(
  [allottees, sharePrice],
  () => {
    const sumShares = (allottees.value || [])
      .map((a) => Number(a?.shares || 0))
      .filter((n) => isFinite(n))
      .reduce((s, n) => s + n, 0);
    totalShares.value = sumShares > 0 ? String(sumShares) : '';
    const price = parseFloat(String(sharePrice.value || '0')) || 0;
    const cash = sumShares * price;
    if (cash > 0) {
      totalCashAmount.value = cash.toFixed(2);
      totalCashAmountWords.value = ringgitToWords(cash);
    } else {
      totalCashAmount.value = '';
      totalCashAmountWords.value = '';
    }
  },
  { deep: true, immediate: true },
);

// ─── 감사인 ───
const auditorName = ref('');
const auditorAddress = ref('');
const auditorContactCd = ref(''); // Person Lookup 으로 회사 contact 선택 시 채워짐 → register 시 contact 재생성 방지
const auditorDateOfAppointment = ref(''); // L_OFFICER.APPOINTMENT_DATE 로 사용 — register 시 적용
const auditorAddresses = ref<any[]>([]);
const auditorAddressSelectedIdx = ref(-1);

// ─── 세무대리인 ───
const taxAgentName = ref('');
const taxAgentAddress = ref('');
const taxAgentAddresses = ref<any[]>([]);
const taxAgentAddressSelectedIdx = ref(-1);
const taxAgentRegistrationBracket = ref('');
const taxAgentEmail = ref('');
const taxAgentApprovalNo = ref('');
const taxAgentOfficerName = ref('');

// ─── LHDN ───
const lhdnRefNo = ref('');
const directorNameForLhdn = ref('');
/** T-Sign 바인딩용 — UI 에 표시하지 않음. lhdnDirector lookup 선택 시 설정. */
const directorEmailForLhdn = ref('');

// ─── 정관 ───
const hasConstitution = ref(false);
const businessObject1 = ref('');
const businessObject2 = ref('');
const businessObject3 = ref('');
const constitutionClauseNo = ref('');

/**
 * MSIC (Malaysia Standard Industrial Classification) 옵션 — Business Object combobox 용.
 *   item-title 로 검색: "01111 - Growing of Maize"  → code / desc 양쪽 검색 가능.
 *   item-value 는 desc 만 (문서 변수 [[business_object_N]] 에는 desc 텍스트가 들어감).
 *   v-combobox 이므로 목록에 없는 free text 도 허용.
 */
interface MsicOption { code: string; desc: string; descMy?: string; title: string }
const msicOptions = excludeCrsUnfilableMsic((msicCodes as any[]) || []).map((m): MsicOption => ({
  code: String(m.code || '').trim(),
  desc: String(m.desc || '').trim(),
  descMy: String(m.descMy || '').trim(),
  title: `${String(m.code || '').trim()} - ${String(m.desc || '').trim()}`,
})).filter((o) => o.code && o.desc);
/**
 * Constitution Clause 옵션 — entity 의 기존 constitution 조항 (L_ARTICLE_NO 마스터,
 *   `/api/entity_my/profile/:cd` 의 resolution.articleNoOptions). AR / AOS 등 다른 PreSet 과 동일 소스.
 *   엔티티에 정관 정보가 없으면 옵션 없음 — v-combobox 이므로 사용자가 free typing 으로 직접 입력.
 */
const entityArticleNoOptions = ref<string[]>([]);
const constitutionClauseOptions = computed<string[]>(() => {
  // entity 옵션 + 현재 선택값(복원/직접입력) 을 항상 항목에 포함 → 콤보박스가 selected 형태로 노출.
  const opts = [...entityArticleNoOptions.value];
  const cur = String(constitutionClauseNo.value || '').trim();
  if (cur && !opts.includes(cur)) opts.unshift(cur);
  return opts;
});
/** Clause No 입력 정규화 — "Article 5" / "Clause 5" / "5" 모두 "5" 로. */
function parseClauseNo(input: string | null): string {
  const s = String(input || '').trim();
  if (!s) return '';
  const m = s.match(/(?:Article|Clause)\s+([\w()]+)/i);
  return m ? m[1] : s;
}
const constitutionClauseNoModel = computed<string>({
  get: () => constitutionClauseNo.value,
  set: (val: string | null) => { constitutionClauseNo.value = parseClauseNo(val); },
});

// ─── Common Seal ───
const hasCommonSeal = ref(false);

// ─── Lodger ───
const lodgerName = ref('');
const lodgerNric = ref('');
const lodgerAddress = ref('');
const lodgerPhone = ref('');
const lodgerEmail = ref('');

// ─── Nominee Director Indemnity ───
/** Letter of Indemnity 발행 여부 — 미체크 시 해당 문서 skip */
const hasNomineeDirector = ref(false);
const nomineeDirectorName = ref('');
const nomineeDirectorAddress = ref('');

// ─── 공통 날짜 ───
// resolutionDate — 3 occurrences, one shared useDateField + 3 separate menu refs
const resolutionDateField = useDateField();
const resolutionDate = resolutionDateField.dateValue;
const resolutionLocalText = resolutionDateField.localText;
const resolutionOnBlur = resolutionDateField.onDateTextBlur;
const resolutionDatePickerValue = resolutionDateField.datePickerValue;
const resolutionDatePickerProps = resolutionDateField.datePickerProps;
const resolutionDatePlaceholder = resolutionDateField.datePlaceholder;
const resolutionMenuFirstBoard = ref(false);
const resolutionMenuConstitution = ref(false);
const resolutionMenuCommonSeal = ref(false);

// letterDate — 5 occurrences, one shared useDateField + 5 separate menu refs
const letterDateField = useDateField();
const letterDate = letterDateField.dateValue;
const letterLocalText = letterDateField.localText;
const letterOnBlur = letterDateField.onDateTextBlur;
const letterDatePickerValue = letterDateField.datePickerValue;
const letterDatePickerProps = letterDateField.datePickerProps;
const letterDatePlaceholder = letterDateField.datePlaceholder;
const letterMenuPettyCash = ref(false);
const letterMenuAuditor = ref(false);
const letterMenuTaxAgent = ref(false);
const letterMenuLhdn = ref(false);
const letterMenuIndemnity = ref(false);

// circulationDate — 1 occurrence
const circulationDateField = useDateField();
const circulationDate = circulationDateField.dateValue;
const circulationDateMenu = circulationDateField.dateMenu;
const circulationDatePickerValue = circulationDateField.datePickerValue;
const circulationDatePickerProps = circulationDateField.datePickerProps;
const circulationLocalText = circulationDateField.localText;
const circulationOnBlur = circulationDateField.onDateTextBlur;
const circulationDatePlaceholder = circulationDateField.datePlaceholder;

// deadlineDate — 1 occurrence
const deadlineDateField = useDateField();
const deadlineDate = deadlineDateField.dateValue;
const deadlineDateMenu = deadlineDateField.dateMenu;
const deadlineDatePickerValue = deadlineDateField.datePickerValue;
const deadlineDatePickerProps = deadlineDateField.datePickerProps;
const deadlineLocalText = deadlineDateField.localText;
const deadlineOnBlur = deadlineDateField.onDateTextBlur;
const deadlineDatePlaceholder = deadlineDateField.datePlaceholder;

// Declaration Date — useDateField: 직접 입력 + 초록 달력 아이콘 트리거 + skip
const declarationDateField = useDateField();
const declarationDate = declarationDateField.dateValue;
const declarationDateMenu = declarationDateField.dateMenu;
const declarationDatePickerValue = declarationDateField.datePickerValue;
const declarationDatePickerProps = declarationDateField.datePickerProps;
const declarationLocalText = declarationDateField.localText;
const declarationOnBlur = declarationDateField.onDateTextBlur;
const declarationDatePlaceholder = declarationDateField.datePlaceholder;

// 단일 필드 skip
const skippedFields = ref<Set<string>>(new Set());
function isFieldSkipped(field: string): boolean { return skippedFields.value.has(field); }
function toggleFieldSkip(field: string, skip: boolean) {
  if (skip) skippedFields.value.add(field);
  else skippedFields.value.delete(field);
}
function valOrBlank(field: string, value: string): string {
  return isFieldSkipped(field) ? '' : value;
}

function isoFromDate(val: Date | null): string {
  if (!val) return '';
  const y = val.getFullYear();
  const m = String(val.getMonth() + 1).padStart(2, '0');
  const d = String(val.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/**
 * entity profile 의 다양한 날짜 포맷을 ISO (YYYY-MM-DD) 로 정규화.
 *   지원:
 *     "2025-12-31" / "2025-12-31 00:00:00"           → 2025-12-31
 *     "31/12/2025" / "31-12-2025"                    → 2025-12-31
 *     "12/31/2025"                                   → 2025-12-31 (US format)
 *     "31 December 2025" / "December 31, 2025"       → 2025-12-31 (Date constructor 시도)
 *     "31 Dec"                                       → 현재 년도 기준 ISO (FYE 가 월일만 저장된 경우)
 */
function normalizeProfileDateToIso(raw: string | Date | null | undefined): string {
  if (!raw) return '';
  if (raw instanceof Date) return isoFromDate(raw);
  const s = String(raw).trim();
  if (!s) return '';

  // 1) YYYY-MM-DD or YYYY-MM-DD HH:mm:ss
  let m = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
  if (m) return `${m[1]}-${m[2].padStart(2, '0')}-${m[3].padStart(2, '0')}`;

  // 2) DD/MM/YYYY or DD-MM-YYYY
  m = s.match(/^(\d{1,2})[\/-](\d{1,2})[\/-](\d{4})$/);
  if (m) {
    const d = parseInt(m[1], 10);
    const mo = parseInt(m[2], 10);
    if (d > 12) {
      // 분명 DD/MM/YYYY
      return `${m[3]}-${String(mo).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
    }
    // 양쪽 가능 — MY 컨벤션 DD/MM/YYYY 우선
    return `${m[3]}-${String(mo).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
  }

  // 3) "DD MMMM" (FYE 가 월일만 저장 — 예: "31 December")
  m = s.match(/^(\d{1,2})\s+([A-Za-z]+)$/);
  if (m) {
    const yyyy = new Date().getFullYear();
    const dParsed = new Date(`${m[2]} ${m[1]}, ${yyyy}`);
    if (!isNaN(dParsed.getTime())) return isoFromDate(dParsed);
  }

  // 4) Date constructor fallback (브라우저 로케일 다양한 포맷 지원)
  const dFallback = new Date(s);
  if (!isNaN(dFallback.getTime())) return isoFromDate(dFallback);
  return '';
}

// ─── Task Config (processSteps → taskFlow 동적 결정) ───
const taskConfigSteps = ref<string[]>([]);
onMounted(async () => {
  const taskCode = props.selection.task.TASK_CODE;
  if (!taskCode) return;
  try {
    const cfg = await getTaskConfigByCode(taskCode);
    taskConfigSteps.value = ((cfg as any)?.processSteps || []) as string[];
  } catch { /* ignore */ }
});

// ─── 사람 lookup 요청 ───
function requestLookup(ctx: PersonLookupContext) {
  emit('request-person-lookup', ctx);
}

// ─── Add/Remove Row 헬퍼 ───
function addDirectorRow() {
  directors.value.push({ contactCd: '', name: '', address: '', isNominee: false });
}
function removeDirectorRow(i: number) {
  directors.value.splice(i, 1);
}
function addCosecRow() {
  cosecs.value.push({
    contactCd: '', name: '', licenseNo: '', nricNo: '', firm: '', tel: '', email: '', fax: '',
    ssmCertNo: '', companyName: '', address: '',
  });
}
function removeCosecRow(i: number) { cosecs.value.splice(i, 1); }
function addAllotteeRow() {
  allottees.value.push({ contactCd: '', name: '', shares: 0 });
}
function removeAllotteeRow(i: number) { allottees.value.splice(i, 1); }

// ─── Drag & Drop ───
const isDragging = ref(false);
function _onGlobalDragStart() { isDragging.value = true; }
function _onGlobalDragEnd() { isDragging.value = false; }
function handleFieldDrop(event: DragEvent, setter: (v: string) => void) {
  isDragging.value = false;
  const json = event.dataTransfer?.getData('application/x-entity-field');
  if (json) {
    try { const { value } = JSON.parse(json); if (value) { setter(String(value)); return; } } catch {}
  }
  const text = event.dataTransfer?.getData('text/plain');
  if (text) setter(String(text));
}
onMounted(() => {
  document.addEventListener('dragstart', _onGlobalDragStart);
  document.addEventListener('dragend', _onGlobalDragEnd);
});
onUnmounted(() => {
  document.removeEventListener('dragstart', _onGlobalDragStart);
  document.removeEventListener('dragend', _onGlobalDragEnd);
});

// ─── Documents → section 매핑 (chip 표시용) ───
const MAX_DOC_CHIPS = 3;
function docsForSection(section: string): Array<{ tmpId?: number; templateName: string }> {
  const docs = (props.selection.documents || []) as any[];
  const match = (re: RegExp) => docs.filter((d) => re.test(String(d?.templateName || '').toLowerCase()));
  switch (section) {
    case 'directors': return match(/first.*board|board.*director|appoint.*co.?sec|petty\s*cash|auditor.*letter|tax\s*agent.*letter|dcr.*constitution|common\s*seal/i);
    case 'cosec': return match(/appoint.*co.?sec|first.*co.?sec|236.?\(?3\)?|petty\s*cash|members.*written|members.*wr/i);
    case 'allottee': return match(/first.*board|board.*director|petty\s*cash/i);
    case 'auditor': return match(/auditor|first.*board|board.*director/i);
    case 'taxAgent': return match(/tax\s*agent|first.*board|board.*director|lhdn/i);
    case 'lhdn': return match(/lhdn|inland\s*revenue/i);
    case 'constitution': return match(/constitution|annexure/i);
    case 'commonSeal': return match(/common\s*seal/i);
    case 'lodger': return match(/annexure|constitution.*annexure/i);
    case 'indemnity': return match(/indemnity/i);
  }
  return [];
}
function docLabel(d: any): string {
  const name = String(d?.templateName || d?.docCode || 'Document');
  return name.replace(/\.docx?$/i, '');
}

/**
 * 각 문서가 사용자 선택으로 인해 skip 될지 판단 — Footer 위의 "Documents to be generated" 섹션 + Create Task 시 필터링에 사용.
 *   - Letter of Indemnity: hasNomineeDirector === false 일 때 skip
 *   - Constitution 관련 (DCR/WR/Annexure): hasConstitution === false 일 때 skip
 *   - DCR Common Seal: hasCommonSeal === false 일 때 skip
 */
function isDocSkipped(templateName: string): { skipped: boolean; reason?: string } {
  const n = String(templateName || '').toLowerCase();
  if (/indemnity/i.test(n) && !hasNomineeDirector.value) {
    return { skipped: true, reason: 'No nominee director' };
  }
  if (/common\s*seal/i.test(n) && !hasCommonSeal.value) {
    return { skipped: true, reason: 'Common Seal not adopted' };
  }
  if (/constitution|annexure|members.*written/i.test(n) && !hasConstitution.value) {
    return { skipped: true, reason: 'Constitution not adopted' };
  }
  return { skipped: false };
}
const skippedDocsCount = computed(() =>
  (props.selection.documents || []).filter((d: any) => isDocSkipped(d?.templateName || '').skipped).length,
);

// ─── 사람 선택 watcher (TODO: 구체 fill 로직 Phase 2 에서 구현) ───
watch(
  () => props.selectedPerson,
  (person) => {
    if (!person) return;
    const ctx = props.activePersonContext;
    if (!ctx) return;
    const fullName = person.name || person.contactName || '';
    const address = person.primaryAddress || '';
    switch (ctx.fieldGroup) {
      case 'director': {
        const i = ctx.index ?? 0;
        if (!directors.value[i]) directors.value[i] = { name: '', address: '' };
        directors.value[i].name = fullName;
        directors.value[i].contactCd = (person as any).contactCd || '';
        directors.value[i].email = String((person as any).contactEmail || '').trim();
        if (Array.isArray((person as any).addresses) && (person as any).addresses.length > 0) {
          const addrs = (person as any).addresses;
          directors.value[i].addresses = addrs;
          // autoBindOfficers 와 동일 규칙(Residential 우선 → Business → 첫 비어있지 않은) 으로
          //   선택 idx까지 설정 — chip의 'selected' 강조가 나오도록.
          const picked = pickDefaultAddress(addrs);
          if (picked.value) {
            directors.value[i].address = picked.value;
            directors.value[i].addressSelectedIdx = picked.idx;
          } else if (address) {
            directors.value[i].address = address;
          }
        } else if (address) {
          directors.value[i].address = address;
        }
        break;
      }
      case 'cosec': {
        const i = ctx.index ?? 0;
        if (!cosecs.value[i]) {
          cosecs.value[i] = {
            name: '', licenseNo: '', nricNo: '', firm: '', tel: '', email: '', fax: '',
            ssmCertNo: '', companyName: '', address: '',
          };
        }
        cosecs.value[i].name = fullName;
        cosecs.value[i].contactCd = (person as any).contactCd || '';
        cosecs.value[i].nricNo = String((person as any).contactRegNo || '').trim();
        // firm = 비서 소속 회사명. businessOccupation(직책) 과 혼동 금지. 사용자가 Co-Sec Firm Name
        //   입력으로 채우므로 lookup 시 firm 값은 건드리지 않음(기존 값 유지).
        cosecs.value[i].tel = String((person as any).mobileNo || '').trim();
        cosecs.value[i].email = String((person as any).contactEmail || '').trim();
        cosecs.value[i].licenseNo = String((person as any).licenseNumber || '').trim();
        cosecs.value[i].ssmCertNo = String((person as any).ssmPractisingCertificateNumber || '').trim();
        cosecs.value[i].address = address;
        break;
      }
      case 'allottee': {
        const i = ctx.index ?? 0;
        if (!allottees.value[i]) allottees.value[i] = { contactCd: '', name: '', shares: 0 };
        allottees.value[i].name = fullName;
        allottees.value[i].contactCd = (person as any).contactCd || '';
        break;
      }
      case 'auditor':
        auditorName.value = fullName;
        auditorContactCd.value = String((person as any).contactCd || '').trim();
        if (Array.isArray((person as any).addresses)) {
          auditorAddresses.value = (person as any).addresses;
          auditorAddressSelectedIdx.value = (person as any).addresses.findIndex((a: any) => a?.value === address);
        }
        if (address) auditorAddress.value = address;
        break;
      case 'auditorAddress':
        if (address) auditorAddress.value = address;
        break;
      case 'taxAgent':
        taxAgentName.value = fullName;
        if (Array.isArray((person as any).addresses)) {
          taxAgentAddresses.value = (person as any).addresses;
          taxAgentAddressSelectedIdx.value = (person as any).addresses.findIndex((a: any) => a?.value === address);
        }
        if (address) taxAgentAddress.value = address;
        break;
      case 'taxAgentAddress':
        if (address) taxAgentAddress.value = address;
        break;
      case 'lodger':
        lodgerName.value = fullName;
        lodgerNric.value = String((person as any).contactRegNo || '').trim();
        if (address) lodgerAddress.value = address;
        lodgerEmail.value = String((person as any).contactEmail || '').trim();
        lodgerPhone.value = String((person as any).mobileNo || '').trim();
        break;
      case 'nomineeDirector':
        nomineeDirectorName.value = fullName;
        if (address) nomineeDirectorAddress.value = address;
        break;
      case 'lhdnDirector':
        directorNameForLhdn.value = fullName;
        directorEmailForLhdn.value = String((person as any).contactEmail || '').trim();
        break;
      case 'cosecFirm':
        cosecFirmName.value = (person as any).contactName || fullName;
        if (address) cosecFirmAddress.value = address;
        break;
    }
  },
);

// ─── 주소 chooser watcher ───
watch(
  () => props.selectedAddress,
  (sel) => {
    if (!sel?.value) return;
    const ctx = props.activeAddressContext;
    if (!ctx) return;
    if (ctx.fieldGroup === 'companyRegistered') {
      registeredOfficeAddress.value = sel.value;
    } else if (ctx.fieldGroup === 'companyBusiness') {
      businessAddress.value = sel.value;
    } else if (ctx.fieldGroup === 'director' && directors.value[ctx.index]) {
      directors.value[ctx.index].address = sel.value;
    } else if (ctx.fieldGroup === 'cosec' && cosecs.value[ctx.index]) {
      cosecs.value[ctx.index].address = sel.value;
    } else if (ctx.fieldGroup === 'cosecFirmAddress') {
      cosecFirmAddress.value = sel.value;
    } else if (ctx.fieldGroup === 'lodger') {
      lodgerAddress.value = sel.value;
    } else if (ctx.fieldGroup === 'nomineeDirector') {
      nomineeDirectorAddress.value = sel.value;
    } else if (ctx.fieldGroup === 'auditor' || ctx.fieldGroup === 'auditorAddress') {
      auditorAddress.value = sel.value;
      auditorAddressSelectedIdx.value = auditorAddresses.value.findIndex((a: any) => a?.value === sel.value);
    } else if (ctx.fieldGroup === 'taxAgent' || ctx.fieldGroup === 'taxAgentAddress') {
      taxAgentAddress.value = sel.value;
      taxAgentAddressSelectedIdx.value = taxAgentAddresses.value.findIndex((a: any) => a?.value === sel.value);
    }
  },
);

// ─── Address chooser 요청 (주소 후보 2개 이상일 때 ✨ 클릭) ───
function requestAddressChoice(fieldGroup: string, index: number, addresses: any[], subjectLabel?: string) {
  if (!Array.isArray(addresses) || addresses.length <= 1) return;
  emit('request-address-lookup', {
    fieldGroup,
    index,
    addresses,
    subjectLabel: subjectLabel || `${fieldGroup} ${index + 1}`,
  });
}

/**
 * 주소 직접 입력 요청 — chat 에 address-input 위젯 (ChatMalaysiaAddressInput) 노출.
 *   사용자가 입력 완료 시 selectedAddress 로 흘러옴 → panel watcher 가 fieldGroup 으로 폼 채움.
 *   bridge 에서 activeAddressContext.directInput === true 분기 사용.
 */
function requestAddressInput(fieldGroup: string, subjectLabel?: string) {
  emit('request-address-lookup', {
    fieldGroup,
    index: 0,
    addresses: [],
    directInput: true,
    subjectLabel: subjectLabel || fieldGroup,
  });
}

/** 한 row 의 주소 suggestion chip 텍스트 — type 표시 + 짧은 미리보기 */
function addressSuggestionLabel(addr: any, idx: number, allAddrs: any[] | undefined): string {
  // 백엔드 유형 라벨(Residential/Service/Business Address) 우선 — 직책/Branch 번호 대신 주소 유형 표시.
  if (addr?.label) return String(addr.label);
  if (!allAddrs) return addr?.type || 'Address';
  if (addr?.type === 'branch') {
    const branchNo = allAddrs.slice(0, idx + 1).filter((a) => a?.type === 'branch').length;
    return `Branch ${branchNo}`;
  }
  const type = String(addr?.type || '').trim();
  return type ? type.charAt(0).toUpperCase() + type.slice(1) : 'Address';
}

/**
 * 주소 비교 — case-insensitive, 공백/콤마/특수문자 정규화 후 비교.
 *   "NO. 50, JALAN KEMPAS UTAMA," vs "NO. 50 JALAN KEMPAS UTAMA" 도 매칭.
 */
function addrEq(a: any, b: any): boolean {
  const norm = (s: any) => String(s || '')
    .toUpperCase()
    .replace(/[,.;]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  const na = norm(a);
  const nb = norm(b);
  return !!na && na === nb;
}

// ─── 폼 검증 ───
const showValidationErrors = ref(false);
const isFormValid = computed(() => {
  if (!props.isReady) return false;
  if (!companyName.value) return false;
  if (directors.value.filter((d) => d?.name?.trim()).length === 0) return false;
  return true;
});

// ─── Create Task (Phase 2 에서 풀 구현) ───
const creating = ref(false);
const progress = ref('');

// 진행 상태는 호스트(PreSetDocGenView)의 공통 중앙 오버레이로 흘려보낸다. (로컬 creating/progress 는 버튼 :loading·인라인 에러용으로 유지)
const presetProgress = usePresetProgress();
watch(creating, (v) => { presetProgress.busy = v; });
watch(progress, (v) => { presetProgress.message = v; });

async function handleCreateTask() {
  // 검증
  if (!props.taskTitle?.trim()) {
    progress.value = 'Error: Task Title is required — please enter it in the chat.';
    return;
  }
  if (!entityCd.value) {
    progress.value = 'Error: Please select an entity in the chat first.';
    return;
  }
  showValidationErrors.value = true;
  if (!isFormValid.value) {
    progress.value = 'Error: Please fill all required fields (Company Name + Directors).';
    return;
  }
  const taskTypeCd = props.selection.task.TASK_CODE;
  if (!taskTypeCd) {
    progress.value = 'Error: Task Code not configured in PreSet.';
    return;
  }

  creating.value = true;
  progress.value = 'Creating task...';

  try {
    // 1) Task 생성
    const steps = (taskConfigSteps.value || []).map((s) => String(s).toLowerCase());
    const taskFlow = {
      createDocument: steps.length === 0 ? true : steps.includes('doc'),
      requestTSign: steps.includes('t-sign'),
      proceedWithEFiling: steps.includes('e-filing'),
    };

    // officerData → TASK_DATA.formData 로 저장. presetForm = Edit 복원 스냅샷.
    const officerData = {
      entity: entityCd.value || '',
      entityName: companyName.value || '',
      entityRegNo: companyRegistrationNo.value || '',
      presetForm: gatherState(),
    };

    let taskId: number | null = null;
    if (editMode.value) {
      // ── Update 분기 ──
      taskId = Number(props.editTaskId) || null;
      if (!taskId) { progress.value = 'Error: invalid edit task id.'; creating.value = false; return; }
      progress.value = 'Updating task...';
      try {
        await fetch(`/api/startTask/tasks/${taskId}/data`, {
          method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
          body: JSON.stringify({ taskData: { formData: officerData } }),
        });
      } catch (err) {
        progress.value = `Error: ${err instanceof Error ? err.message : 'Task update failed'}`;
        creating.value = false; return;
      }
      // 제목 갱신 (선택)
      try {
        await fetch(`/api/startTask/tasks/${taskId}/title`, {
          method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
          body: JSON.stringify({ taskTitle: props.taskTitle }),
        });
      } catch { /* title optional */ }
      progress.value = `Updating documents (Task ${taskId})...`;
    } else {
      // ── Create 분기 ──
      const taskRes = await fetch('/api/startTask/tasks', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          entityCd: entityCd.value || null,
          taskTypeCd,
          taskTypeName: props.selection.task.TASK_NAME,
          taskTitle: props.taskTitle,
          taskFlow,
          officerData,
        }),
      });
      const taskJson = await taskRes.json();
      taskId = taskJson?.data?.taskId || taskJson?.taskId || null;
      if (!taskId) {
        progress.value = `Error: ${taskJson?.error?.message || taskJson?.message || 'Task creation failed'}`;
        creating.value = false;
        return;
      }
      progress.value = `Task created (ID: ${taskId}). Saving documents...`;
    }

    // 2) 변수 합성
    const commonFormData = buildIncorporationPostVariables({
      companyName: companyName.value,
      companyRegistrationNo: companyRegistrationNo.value,
      registeredOfficeAddress: registeredOfficeAddress.value,
      businessAddress: businessAddress.value,
      companyTel: companyTel.value,
      incorporationDate: valOrBlank('incorporationDate', incorporationDate.value),
      directors: directors.value,
      isSoleDirector: isSoleDirector.value,
      cosecs: cosecs.value.map((c, i) =>
        i === 0 ? { ...c, companyName: cosecFirmName.value, address: cosecFirmAddress.value } : c
      ),
      financialYearEnd: financialYearEnd.value,
      firstAccountFrom: firstAccountFrom.value,
      firstAccountTo: firstAccountTo.value,
      totalShares: totalShares.value,
      sharePrice: sharePrice.value,
      totalCashAmount: totalCashAmount.value,
      totalCashAmountWords: totalCashAmountWords.value,
      allottees: allottees.value,
      auditorName: auditorName.value,
      auditorAddress: auditorAddress.value,
      auditorContactCd: auditorContactCd.value,
      auditorDateOfAppointment: auditorDateOfAppointment.value,
      taxAgentName: taxAgentName.value,
      taxAgentAddress: taxAgentAddress.value,
      taxAgentRegistrationBracket: taxAgentRegistrationBracket.value,
      taxAgentEmail: taxAgentEmail.value,
      taxAgentApprovalNo: taxAgentApprovalNo.value,
      taxAgentOfficerName: taxAgentOfficerName.value,
      lhdnRefNo: lhdnRefNo.value,
      directorNameForLhdn: directorNameForLhdn.value,
      hasConstitution: hasConstitution.value,
      businessObject1: businessObject1.value,
      businessObject2: businessObject2.value,
      businessObject3: businessObject3.value,
      constitutionClauseNo: constitutionClauseNo.value,
      hasCommonSeal: hasCommonSeal.value,
      lodger: lodgerName.value
        ? {
          name: lodgerName.value, nricNo: lodgerNric.value, address: lodgerAddress.value,
          phoneNo: lodgerPhone.value, email: lodgerEmail.value,
        }
        : undefined,
      nomineeDirectorName: nomineeDirectorName.value,
      nomineeDirectorAddress: nomineeDirectorAddress.value,
      resolutionDate: valOrBlank('resolutionDate', resolutionDate.value),
      letterDate: valOrBlank('letterDate', letterDate.value),
      declarationDate: valOrBlank('declarationDate', declarationDate.value),
      circulationDate: valOrBlank('circulationDate', circulationDate.value),
      deadlineDate: valOrBlank('deadlineDate', deadlineDate.value),
    });

    // 3) 문서 저장 (skip 된 문서 제외)
    // 헬퍼: per-cosec 변수 override — Section 236(3) 등 비서별 N장 복제용.
    //   mapper 가 primaryCosec 기준으로 채운 cosec_* 변수를 해당 cosec 데이터로 덮어씀.
    const formatMalaysiaNricForDoc = (raw: any) => {
      const s = String(raw ?? '').trim();
      if (!s) return '';
      const digits = s.replace(/\D/g, '');
      if (digits.length === 12) {
        return `${digits.slice(0, 6)}-${digits.slice(6, 8)}-${digits.slice(8)}`;
      }
      return s;
    };
    const buildCosecOverrideVars = (c: any) => ({
      cosec_name: String(c?.name || '').trim(),
      cosec_nric_no: formatMalaysiaNricForDoc(c?.nricNo),
      cosec_firm: String(c?.firm || '').trim(),
      cosec_tel: String(c?.tel || '').trim(),
      cosec_email: String(c?.email || '').trim(),
      cosec_fax: String(c?.fax || '').trim(),
      cosec_license_no: String(c?.licenseNo || '').trim(),
      cosec_ssm_cert_no: String(c?.ssmCertNo || '').trim(),
      cosec_company_name: String(c?.companyName || c?.firm || '').trim(),
      cosec_address: String(c?.address || '').trim(),
    });

    const docsToSave = (props.selection.documents || [])
      .filter((d: any) => d?.tmpId)
      .filter((d: any) => {
        const r = isDocSkipped(d?.templateName || '');
        if (r.skipped) {
          console.info('[IncorporationPost] Skipping doc:', d?.templateName, '—', r.reason);
          return false;
        }
        return true;
      })
      // flatMap: Section 236(3) 은 비서 수만큼 복제, 그 외 문서는 1장.
      //   <any> 명시 — Section 236(3) 분기와 다른 분기의 formData 모양이 달라 TS 가 union 추론 실패.
      .flatMap<any>((d: any) => {
        const tName = String(d.templateName || '').toLowerCase();
        const primaryDir = directors.value.find((dir) => dir?.name?.trim());
        const primaryCosec = cosecs.value.find((c) => c?.name?.trim());
        const validCosecs = cosecs.value.filter((c) => c?.name?.trim());

        // 전원 사이너 후보 — 이름 있는 항목만, T-Sign 에서 수정 가능.
        const allDirSigners = directors.value
          .filter((dir) => dir?.name?.trim())
          .map((dir) => ({
            name: String(dir.name).trim(),
            email: String((dir as any).email || '').trim(),
            role: 'signer',
          }));
        const allMemberSigners = allottees.value
          .filter((a) => a?.name?.trim())
          .map((a) => ({
            name: String(a.name).trim(),
            email: '',
            role: 'signer',
          }));

        // Section 236(3) — 비서 수만큼 N장 복제. 각 문서의 cosec_* 변수를 해당 비서로 override + 본인 서명.
        if (/236.?\(?3\)?|section\s*236/i.test(tName)) {
          // 비서가 1명도 없으면 1장만 생성 (primaryCosec 도 없는 케이스 — 변수 비어있는 상태로 진행).
          const targets = validCosecs.length > 0 ? validCosecs : [primaryCosec].filter(Boolean);
          if (targets.length === 0) {
            return [{
              templateId: d.tmpId,
              label: d.templateName,
              personName: companyName.value || '',
              formData: { ...commonFormData },
              entityCd: entityCd.value || undefined,
              signers: undefined,
            }];
          }
          return targets.map((c: any) => ({
            templateId: d.tmpId,
            // 비서가 2명 이상일 때 라벨에 비서명 추가 — 미리보기/리스트에서 구분.
            label: targets.length > 1 ? `${d.templateName} — ${c?.name || ''}` : d.templateName,
            personName: c?.name || companyName.value || '',
            formData: { ...commonFormData, ...buildCosecOverrideVars(c) },
            entityCd: entityCd.value || undefined,
            signers: c?.name
              ? [{ name: String(c.name).trim(), email: String(c.email || '').trim(), role: 'signer' }]
              : undefined,
          }));
        }

        // 문서별 자동 사이너 설정 — 매칭 안 되는 문서는 undefined(T-Sign 에서 수동).
        let signers: Array<{ name: string; email: string; role: string }> | undefined;

        if (/lhdn|inland\s*revenue/i.test(tName)) {
          // LHDN 서신 — AI 버튼으로 선택한 1명 이사 (또는 primary director fallback)
          const lhdnName = directorNameForLhdn.value || primaryDir?.name || '';
          const lhdnEmail = directorEmailForLhdn.value || '';
          if (lhdnName) {
            signers = [{ name: lhdnName, email: lhdnEmail, role: 'signer' }];
          }
        } else if (/appointment\s*letter.*auditor/i.test(tName)) {
          // Appointment Letter (Auditors) — Signing Director 1명 (primary director, 변경 가능)
          if (primaryDir?.name) {
            signers = [{ name: primaryDir.name.trim(), email: (primaryDir.email || '').trim(), role: 'signer' }];
          }
        } else if (/appointment\s*letter.*tax\s*agent|tax\s*agent.*appointment/i.test(tName)) {
          // Appointment Letter (Tax Agent) — Signing Director 1명
          if (primaryDir?.name) {
            signers = [{ name: primaryDir.name.trim(), email: (primaryDir.email || '').trim(), role: 'signer' }];
          }
        } else if (/petty\s*cash/i.test(tName)) {
          // Petty Cash Certificate — Directors 전원
          if (allDirSigners.length) signers = allDirSigners;
        } else if (/members.{0,3}written\s*resolution|member.{0,3}written/i.test(tName)) {
          // Members' Written Resolution (Adoption of Constitution) — Members/Shareholders 전원
          if (allMemberSigners.length) signers = allMemberSigners;
        } else if (/constitution.*annexure|annexure.*constitution/i.test(tName)) {
          // Constitution (Annexure A) — Members/Shareholders 전원
          if (allMemberSigners.length) signers = allMemberSigners;
        } else if (/first\s*board.*director|board.*director.*meeting|board.*meeting/i.test(tName)) {
          // First Board Director Meeting — Directors 전원
          if (allDirSigners.length) signers = allDirSigners;
        } else if (/director.*circular\s*resolution|directors.*circular|dcr/i.test(tName)) {
          // Directors' Circular Resolution (Co-Sec / Constitution / Common Seal 등) — Directors 전원
          if (allDirSigners.length) signers = allDirSigners;
        } else if (/letter\s*of\s*indemnity|indemnity|nominee\s*director/i.test(tName)) {
          // Letter of Indemnity (in case have nominee director) — Directors 전원이 indemnify 측 서명.
          if (allDirSigners.length) signers = allDirSigners;
        }

        // flatMap 이라 단일 결과도 배열로 감싸서 반환.
        return [{
          templateId: d.tmpId,
          label: d.templateName,
          personName: companyName.value || '',
          formData: { ...commonFormData },
          entityCd: entityCd.value || undefined,
          signers: signers || undefined,
        }];
      });

    if (docsToSave.length > 0) {
      try {
        const saveRes = await fetch('/api/aiDocuments/incorporation/prepare-documents', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          // Edit 모드: replaceExisting → 기존 task 문서 삭제 후 재생성. create 모드: 신규 INSERT.
          body: JSON.stringify({ taskId, documents: docsToSave, replaceExisting: editMode.value }),
        });
        const saveJson = await saveRes.json();
        if (!saveJson?.success) {
          progress.value = `Documents save warning: ${saveJson?.message || 'unknown'}`;
        }
      } catch (err) {
        console.warn('[IncorporationPost] Document save failed:', err);
      }
    }

    // 4) document-status 업데이트
    try {
      await fetch(`/api/startTask/tasks/${taskId}/document-status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });
    } catch { /* ignore */ }

    // 5) PreSet 사용 로그
    try {
      await fetch('/api/startTask/preset/use-log', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ presetTaskId: props.selection.task.PRESET_TASK_ID, taskId }),
      });
    } catch { /* ignore */ }

    // 6) 다음 페이지 prefetch
    try {
      progress.value = 'Preparing next screen...';
      const [taskDetailRes, docListRes] = await Promise.all([
        fetch(`/api/startTask/tasks/${taskId}`, { credentials: 'include' }).then((r) => r.json()).catch(() => null),
        fetch(`/api/startTask/doc-cust-save/${taskId}`, { credentials: 'include' }).then((r) => r.json()).catch(() => null),
      ]);
      sessionStorage.setItem(
        `presetTaskDetailPrefetch:${taskId}`,
        JSON.stringify({ taskDetail: taskDetailRes, docList: docListRes }),
      );
    } catch { /* ignore */ }

    emit('task-created', { taskId, taskTitle: props.taskTitle });
    router.push({ name: 'PreSetTaskDetail', query: { taskId: String(taskId) } });
  } catch (err: any) {
    console.error('[IncorporationPost] create task failed:', err);
    progress.value = `Error: ${err?.message || 'Failed to create task.'}`;
  } finally {
    creating.value = false;
  }
}

// ─── Edit 지원: 전체 입력 스냅샷 gather/restore (호스트가 TASK_DATA.formData.presetForm 로 저장/복원) ───
function gatherState(): Record<string, any> {
  return {
    // 회사
    companyName: companyName.value,
    companyRegistrationNo: companyRegistrationNo.value,
    registeredOfficeAddress: registeredOfficeAddress.value,
    businessAddress: businessAddress.value,
    companyTel: companyTel.value,
    incorporationDate: incorporationDate.value,
    // 이사 / 비서 / 주주
    directors: JSON.parse(JSON.stringify(directors.value || [])),
    isSoleDirectorOverride: isSoleDirectorOverride.value,
    cosecs: JSON.parse(JSON.stringify(cosecs.value || [])),
    cosecFirmName: cosecFirmName.value,
    cosecFirmAddress: cosecFirmAddress.value,
    allottees: JSON.parse(JSON.stringify(allottees.value || [])),
    // 회계
    financialYearEnd: financialYearEnd.value,
    firstAccountFrom: firstAccountFrom.value,
    firstAccountTo: firstAccountTo.value,
    // 주식
    totalShares: totalShares.value,
    sharePrice: sharePrice.value,
    totalCashAmount: totalCashAmount.value,
    totalCashAmountWords: totalCashAmountWords.value,
    // 감사인
    auditorName: auditorName.value,
    auditorAddress: auditorAddress.value,
    auditorContactCd: auditorContactCd.value,
    auditorDateOfAppointment: auditorDateOfAppointment.value,
    auditorAddresses: JSON.parse(JSON.stringify(auditorAddresses.value || [])),
    auditorAddressSelectedIdx: auditorAddressSelectedIdx.value,
    // 세무대리인
    taxAgentName: taxAgentName.value,
    taxAgentAddress: taxAgentAddress.value,
    taxAgentAddresses: JSON.parse(JSON.stringify(taxAgentAddresses.value || [])),
    taxAgentAddressSelectedIdx: taxAgentAddressSelectedIdx.value,
    taxAgentRegistrationBracket: taxAgentRegistrationBracket.value,
    taxAgentEmail: taxAgentEmail.value,
    taxAgentApprovalNo: taxAgentApprovalNo.value,
    taxAgentOfficerName: taxAgentOfficerName.value,
    // LHDN
    lhdnRefNo: lhdnRefNo.value,
    directorNameForLhdn: directorNameForLhdn.value,
    directorEmailForLhdn: directorEmailForLhdn.value,
    // 정관
    hasConstitution: hasConstitution.value,
    businessObject1: businessObject1.value,
    businessObject2: businessObject2.value,
    businessObject3: businessObject3.value,
    constitutionClauseNo: constitutionClauseNo.value,
    // Common Seal
    hasCommonSeal: hasCommonSeal.value,
    // Lodger
    lodgerName: lodgerName.value,
    lodgerNric: lodgerNric.value,
    lodgerAddress: lodgerAddress.value,
    lodgerPhone: lodgerPhone.value,
    lodgerEmail: lodgerEmail.value,
    // Nominee Director Indemnity
    hasNomineeDirector: hasNomineeDirector.value,
    nomineeDirectorName: nomineeDirectorName.value,
    nomineeDirectorAddress: nomineeDirectorAddress.value,
    // 공통 날짜
    resolutionDate: resolutionDate.value,
    letterDate: letterDate.value,
    circulationDate: circulationDate.value,
    deadlineDate: deadlineDate.value,
    declarationDate: declarationDate.value,
    // 필드 skip
    skippedFields: Array.from(skippedFields.value),
  };
}

function restoreState(s: Record<string, any> | null | undefined) {
  if (!s || typeof s !== 'object') return;
  // 회사
  if (s.companyName != null) companyName.value = s.companyName;
  if (s.companyRegistrationNo != null) companyRegistrationNo.value = s.companyRegistrationNo;
  if (s.registeredOfficeAddress != null) registeredOfficeAddress.value = s.registeredOfficeAddress;
  if (s.businessAddress != null) businessAddress.value = s.businessAddress;
  if (s.companyTel != null) companyTel.value = s.companyTel;
  if (s.incorporationDate != null) incorporationDate.value = s.incorporationDate;
  // 이사 / 비서 / 주주
  if (Array.isArray(s.directors)) directors.value = s.directors;
  if (s.isSoleDirectorOverride != null) isSoleDirectorOverride.value = s.isSoleDirectorOverride;
  if (Array.isArray(s.cosecs)) cosecs.value = s.cosecs;
  if (s.cosecFirmName != null) cosecFirmName.value = s.cosecFirmName;
  if (s.cosecFirmAddress != null) cosecFirmAddress.value = s.cosecFirmAddress;
  if (Array.isArray(s.allottees)) allottees.value = s.allottees;
  // 회계
  if (s.financialYearEnd != null) financialYearEnd.value = s.financialYearEnd;
  if (s.firstAccountFrom != null) firstAccountFrom.value = s.firstAccountFrom;
  if (s.firstAccountTo != null) firstAccountTo.value = s.firstAccountTo;
  // 주식
  if (s.totalShares != null) totalShares.value = s.totalShares;
  if (s.sharePrice != null) sharePrice.value = s.sharePrice;
  if (s.totalCashAmount != null) totalCashAmount.value = s.totalCashAmount;
  if (s.totalCashAmountWords != null) totalCashAmountWords.value = s.totalCashAmountWords;
  // 감사인
  if (s.auditorName != null) auditorName.value = s.auditorName;
  if (s.auditorAddress != null) auditorAddress.value = s.auditorAddress;
  if (s.auditorContactCd != null) auditorContactCd.value = s.auditorContactCd;
  if (s.auditorDateOfAppointment != null) auditorDateOfAppointment.value = s.auditorDateOfAppointment;
  if (Array.isArray(s.auditorAddresses)) auditorAddresses.value = s.auditorAddresses;
  if (s.auditorAddressSelectedIdx != null) auditorAddressSelectedIdx.value = Number(s.auditorAddressSelectedIdx);
  // 세무대리인
  if (s.taxAgentName != null) taxAgentName.value = s.taxAgentName;
  if (s.taxAgentAddress != null) taxAgentAddress.value = s.taxAgentAddress;
  if (Array.isArray(s.taxAgentAddresses)) taxAgentAddresses.value = s.taxAgentAddresses;
  if (s.taxAgentAddressSelectedIdx != null) taxAgentAddressSelectedIdx.value = Number(s.taxAgentAddressSelectedIdx);
  if (s.taxAgentRegistrationBracket != null) taxAgentRegistrationBracket.value = s.taxAgentRegistrationBracket;
  if (s.taxAgentEmail != null) taxAgentEmail.value = s.taxAgentEmail;
  if (s.taxAgentApprovalNo != null) taxAgentApprovalNo.value = s.taxAgentApprovalNo;
  if (s.taxAgentOfficerName != null) taxAgentOfficerName.value = s.taxAgentOfficerName;
  // LHDN
  if (s.lhdnRefNo != null) lhdnRefNo.value = s.lhdnRefNo;
  if (s.directorNameForLhdn != null) directorNameForLhdn.value = s.directorNameForLhdn;
  if (s.directorEmailForLhdn != null) directorEmailForLhdn.value = s.directorEmailForLhdn;
  // 정관
  if (typeof s.hasConstitution === 'boolean') hasConstitution.value = s.hasConstitution;
  if (s.businessObject1 != null) businessObject1.value = s.businessObject1;
  if (s.businessObject2 != null) businessObject2.value = s.businessObject2;
  if (s.businessObject3 != null) businessObject3.value = s.businessObject3;
  if (s.constitutionClauseNo != null) constitutionClauseNo.value = s.constitutionClauseNo;
  // Common Seal
  if (typeof s.hasCommonSeal === 'boolean') hasCommonSeal.value = s.hasCommonSeal;
  // Lodger
  if (s.lodgerName != null) lodgerName.value = s.lodgerName;
  if (s.lodgerNric != null) lodgerNric.value = s.lodgerNric;
  if (s.lodgerAddress != null) lodgerAddress.value = s.lodgerAddress;
  if (s.lodgerPhone != null) lodgerPhone.value = s.lodgerPhone;
  if (s.lodgerEmail != null) lodgerEmail.value = s.lodgerEmail;
  // Nominee Director Indemnity
  if (typeof s.hasNomineeDirector === 'boolean') hasNomineeDirector.value = s.hasNomineeDirector;
  if (s.nomineeDirectorName != null) nomineeDirectorName.value = s.nomineeDirectorName;
  if (s.nomineeDirectorAddress != null) nomineeDirectorAddress.value = s.nomineeDirectorAddress;
  // 공통 날짜
  if (s.resolutionDate != null) resolutionDate.value = s.resolutionDate;
  if (s.letterDate != null) letterDate.value = s.letterDate;
  if (s.circulationDate != null) circulationDate.value = s.circulationDate;
  if (s.deadlineDate != null) deadlineDate.value = s.deadlineDate;
  if (s.declarationDate != null) declarationDate.value = s.declarationDate;
  // 필드 skip
  if (Array.isArray(s.skippedFields)) skippedFields.value = new Set(s.skippedFields);
  // 파생 watcher(share auto-sync 등)가 정착한 뒤 기준선 캡처 → 이후 실제 변경만 dirty 로 감지.
  nextTick(() => { editBaseline.value = JSON.stringify(gatherState()); });
}

defineExpose({ gatherState, restoreState });
</script>

<template>
  <div class="ip-panel">
    <div class="ip-header">
      <div>
        <div class="ip-title">{{ selection.task.TASK_NAME }}</div>
        <div class="ip-subtitle">Incorporation (Post)</div>
      </div>
      <v-btn variant="text" size="small" @click="emit('back')">
        <v-icon start size="16">mdi-arrow-left</v-icon>Back
      </v-btn>
    </div>

    <!-- Task Title 박스 -->
    <div class="ip-section ip-sec--slate">
      <div class="ip-section-header"><v-icon size="16" class="mr-1">mdi-tag</v-icon>Task Title</div>
      <div class="ip-readonly-row">
        <div class="ip-readonly-text">{{ taskTitle || '—' }}</div>
        <v-btn variant="tonal" size="small" @click="emit('change-task-title')">Change</v-btn>
      </div>
    </div>

    <!-- Entity 카드 -->
    <div class="ip-entity-card">
      <div class="ip-entity-header">
        <v-icon size="20" color="primary" class="mr-2">mdi-domain</v-icon>
        <div class="ip-entity-info">
          <div class="ip-entity-name">{{ companyName || 'No entity selected' }}</div>
          <div v-if="companyRegistrationNo" class="ip-entity-meta">
            Registration No: <strong>{{ companyRegistrationNo }}</strong>
          </div>
          <div v-if="incorporationDate" class="ip-entity-meta">
            Incorporation Date: <strong>{{ incorporationLocalText || incorporationDate }}</strong>
          </div>
        </div>
        <v-btn variant="text" size="small" @click="emit('change-entity')">Change</v-btn>
      </div>
    </div>

    <!-- ─── 진입 가이드 (Task Title / Entity 미선택 시) ─── -->
    <div v-if="!taskTitle || !entityCd" class="ip-empty-guide">
      <v-icon size="36" color="grey-lighten-1" class="mb-2">mdi-arrow-up-circle-outline</v-icon>
      <div class="ip-empty-title">
        {{ !taskTitle ? 'Please enter a Task Title in the chat first.' : 'Please select an Entity in the chat to begin.' }}
      </div>
      <div class="ip-empty-sub">Document forms will appear after entity is selected.</div>
    </div>

    <!-- ─── Entity prefill 로딩바 ─── -->
    <div v-else-if="entityLoading" class="ip-loading">
      <v-progress-circular indeterminate size="36" width="3" color="primary" />
      <div class="ip-loading-text">Loading entity data — Directors / Secretaries / Members / addresses…</div>
    </div>

    <!-- ─── 모든 폼 섹션 — Task Title + Entity 선택 + prefill 완료 시 노출 ─── -->
    <template v-else>

    <!-- Phase 1 — 회사 정보 (entity 선택 시 자동 prefill, 사용자 수정 가능) -->
    <div class="ip-section ip-sec--blue">
      <div class="ip-section-header">
        <v-icon size="16" class="mr-1">mdi-office-building</v-icon>Company Information
      </div>
      <v-row dense>
        <v-col cols="12" md="6">
          <v-text-field v-model="companyName" label="Company Name *" variant="outlined" density="compact" hide-details />
        </v-col>
        <v-col cols="12" md="6">
          <v-text-field v-model="companyRegistrationNo" label="Registration No *" variant="outlined" density="compact" hide-details />
        </v-col>
        <v-col cols="12">
          <StructuredAddressInput
            v-model="registeredOfficeAddress"
            label="Registered Office Address"
            :dragging-active="isDragging"
            :addresses="entityAddresses"
            @drop-fill="(v) => registeredOfficeAddress = v"
            @show-addresses="emit('request-address-lookup', {
              fieldGroup: 'companyRegistered',
              index: 0,
              addresses: entityAddresses,
              subjectLabel: 'Company Registered Office Address',
            })"
          />
        </v-col>
        <v-col cols="12">
          <StructuredAddressInput
            v-model="businessAddress"
            label="Business Address"
            :dragging-active="isDragging"
            :addresses="entityAddresses"
            @drop-fill="(v) => businessAddress = v"
            @show-addresses="emit('request-address-lookup', {
              fieldGroup: 'companyBusiness',
              index: 0,
              addresses: entityAddresses,
              subjectLabel: 'Company Business Address',
            })"
          />
        </v-col>
        <v-col cols="12" md="6">
          <v-text-field v-model="companyTel" label="Company Tel" variant="outlined" density="compact" hide-details />
        </v-col>
        <v-col cols="12" md="6">
          <v-text-field
            v-model="incorporationLocalText"
            :placeholder="incorporationDatePlaceholder"
            label="Incorporation Date"
            variant="outlined" density="compact" hide-details="auto"
            :class="{ 'ip-field-skipped': isFieldSkipped('incorporationDate') }"
            @blur="incorporationOnBlur($event.target.value)"
          >
            <template v-if="!isFieldSkipped('incorporationDate')" #prepend-inner>
              <v-tooltip location="top" :open-delay="150">
                <template #activator="{ props: tipProps }">
                  <span v-bind="tipProps" class="ip-skip-btn"
                    @mousedown.prevent @click.stop="toggleFieldSkip('incorporationDate', true)">
                    <v-icon size="14" color="grey-lighten-1">mdi-close</v-icon>
                  </span>
                </template>
                Skip — fill before lodging
              </v-tooltip>
            </template>
            <template #append-inner>
              <a v-if="isFieldSkipped('incorporationDate')" class="ip-restore-link"
                @click.stop="toggleFieldSkip('incorporationDate', false)">Restore</a>
              <v-menu v-else v-model="incorporationDateMenu" :close-on-content-click="false" location="bottom">
                <template #activator="{ props: menuProps }">
                  <v-icon v-bind="menuProps" color="success" size="20" style="cursor:pointer">mdi-calendar</v-icon>
                </template>
                <v-date-picker
                  v-bind="incorporationDatePickerProps"
                  v-model="incorporationDatePickerValue"
                  @update:model-value="incorporationDateMenu = false"
                />
              </v-menu>
            </template>
          </v-text-field>
        </v-col>
        <v-col cols="12" md="6">
          <v-text-field
            v-model="financialYearEndLocalText"
            :placeholder="fyeSuggestedPlaceholder"
            label="Financial Year End"
            variant="outlined" density="compact" hide-details="auto"
            :class="{ 'ip-field-skipped': isFieldSkipped('financialYearEnd') }"
            @blur="financialYearEndOnBlur($event.target.value)"
          >
            <template v-if="!isFieldSkipped('financialYearEnd')" #prepend-inner>
              <v-tooltip location="top" :open-delay="150">
                <template #activator="{ props: tipProps }">
                  <span v-bind="tipProps" class="ip-skip-btn"
                    @mousedown.prevent @click.stop="toggleFieldSkip('financialYearEnd', true)">
                    <v-icon size="14" color="grey-lighten-1">mdi-close</v-icon>
                  </span>
                </template>
                Skip — fill before lodging
              </v-tooltip>
            </template>
            <template #append-inner>
              <a v-if="isFieldSkipped('financialYearEnd')" class="ip-restore-link"
                @click.stop="toggleFieldSkip('financialYearEnd', false)">Restore</a>
              <v-menu v-else v-model="financialYearEndMenu" :close-on-content-click="false" location="bottom">
                <template #activator="{ props: menuProps }">
                  <v-icon v-bind="menuProps" color="success" size="20" style="cursor:pointer">mdi-calendar</v-icon>
                </template>
                <v-date-picker
                  v-bind="financialYearEndPickerProps"
                  v-model="financialYearEndPickerValue"
                  @update:model-value="financialYearEndMenu = false"
                />
              </v-menu>
            </template>
          </v-text-field>
        </v-col>
      </v-row>
    </div>

    <!-- ─── Directors ─── -->
    <div class="ip-section ip-sec--green">
      <div class="ip-section-header">
        <v-icon size="16" class="mr-1">mdi-account-tie</v-icon>Directors
        <span class="ip-chip-applies">Applies to:</span>
        <v-chip v-for="d in docsForSection('directors').slice(0, MAX_DOC_CHIPS)" :key="d.tmpId" size="x-small" variant="tonal" color="primary" class="mx-1">
          {{ docLabel(d) }}
        </v-chip>
        <v-tooltip v-if="docsForSection('directors').length > MAX_DOC_CHIPS" location="bottom" :open-delay="100">
          <template #activator="{ props: tProps }">
            <span v-bind="tProps" class="ip-more-chip">+{{ docsForSection('directors').length - MAX_DOC_CHIPS }}</span>
          </template>
          <div class="ip-more-tooltip">
            <div v-for="d in docsForSection('directors').slice(MAX_DOC_CHIPS)" :key="d.tmpId">{{ docLabel(d) }}</div>
          </div>
        </v-tooltip>
      </div>
      <v-radio-group v-model="isSoleDirectorOverride" inline density="compact" hide-details class="mb-2">
        <v-radio label="Auto (1 person → Sole)" value="auto" />
        <v-radio label="Sole Director" value="sole" />
        <v-radio label="Multiple Directors" value="multi" />
      </v-radio-group>
      <v-row v-for="(d, i) in directors" :key="`dir-${i}`" dense align="center">
        <v-col cols="12" md="4">
          <div class="ip-drop-wrap">
            <v-text-field v-model="d.name" :label="`Director ${i + 1} Name${i === 0 ? ' *' : ''}`" variant="outlined" density="compact" hide-details>
              <template #append-inner>
                <v-tooltip location="top" :open-delay="150">
                  <template #activator="{ props: tProps }">
                    <v-btn v-bind="tProps" icon="mdi-creation" size="x-small" variant="text" color="primary"
                      @click="requestLookup({ fieldGroup: 'director', index: i, defaultRole: directors[i]?.contactCd ? 'DIRECTOR' : 'CONTACT' })" />
                  </template>
                  <span>{{ directors[i]?.contactCd ? 'Lookup director from chat' : 'Pick contact from chat' }}</span>
                </v-tooltip>
              </template>
            </v-text-field>
            <div v-if="isDragging" class="ip-drop-overlay" @dragover.prevent
              @drop.prevent="handleFieldDrop($event, (v) => d.name = v)" />
          </div>
        </v-col>
        <v-col cols="12" md="7">
          <div class="ip-drop-wrap">
            <v-text-field v-model="d.address" label="Address (optional)" variant="outlined" density="compact" hide-details>
              <template v-if="(d.addresses?.length ?? 0) > 1" #append-inner>
                <v-tooltip location="top" :open-delay="150">
                  <template #activator="{ props: tProps }">
                    <v-btn v-bind="tProps" icon="mdi-creation" size="x-small" variant="text" color="primary"
                      @click="requestAddressChoice('director', i, d.addresses || [], d.name)" />
                  </template>
                  <span>Choose another address ({{ d.addresses?.length }} options)</span>
                </v-tooltip>
              </template>
            </v-text-field>
            <div v-if="isDragging" class="ip-drop-overlay" @dragover.prevent
              @drop.prevent="handleFieldDrop($event, (v) => d.address = v)" />
            <!-- 후보 chip — index 기반 선택 (같은 value 의 다른 type 도 구분). -->
            <div v-if="(d.addresses?.length ?? 0) >= 1" class="ip-addr-suggest">
              <v-chip
                v-for="(addr, ai) in d.addresses"
                :key="`d-${i}-addr-${ai}`"
                size="x-small"
                variant="tonal"
                :color="d.addressSelectedIdx === ai ? 'primary' : 'default'"
                @click="() => { d.address = addr.value; d.addressSelectedIdx = ai; }"
              >
                {{ addressSuggestionLabel(addr, ai, d.addresses) }}
              </v-chip>
            </div>
          </div>
        </v-col>
        <v-col cols="12" md="1" class="d-flex justify-end">
          <v-btn icon="mdi-close" size="x-small" variant="text" color="error" @click="removeDirectorRow(i)" />
        </v-col>
      </v-row>
      <v-btn variant="tonal" size="small" prepend-icon="mdi-plus" class="mt-2" @click="addDirectorRow">Add Director</v-btn>
    </div>

    <!-- ─── Co-Sec ─── -->
    <div v-if="docFlags.hasDcrAppointCosec || docFlags.hasSection236_3 || docFlags.hasPettyCash || docFlags.hasMembersWrConstitution" class="ip-section ip-sec--purple">
      <div class="ip-section-header">
        <v-icon size="16" class="mr-1">mdi-briefcase-account</v-icon>Company Secretary
        <span class="ip-chip-applies">Applies to:</span>
        <v-chip v-for="d in docsForSection('cosec').slice(0, MAX_DOC_CHIPS)" :key="d.tmpId" size="x-small" variant="tonal" color="primary" class="mx-1">
          {{ docLabel(d) }}
        </v-chip>
        <v-tooltip v-if="docsForSection('cosec').length > MAX_DOC_CHIPS" location="bottom" :open-delay="100">
          <template #activator="{ props: tProps }">
            <span v-bind="tProps" class="ip-more-chip">+{{ docsForSection('cosec').length - MAX_DOC_CHIPS }}</span>
          </template>
          <div class="ip-more-tooltip">
            <div v-for="d in docsForSection('cosec').slice(MAX_DOC_CHIPS)" :key="d.tmpId">{{ docLabel(d) }}</div>
          </div>
        </v-tooltip>
      </div>
      <div v-for="(c, i) in cosecs" :key="`cosec-${i}`" class="ip-subrow">
        <div class="ip-subrow-header">
          <v-icon size="14" color="primary" class="mr-1">mdi-account</v-icon>
          <strong>Co-Sec {{ i + 1 }}</strong>
          <v-spacer />
          <v-btn icon="mdi-close" size="x-small" variant="text" color="error" @click="removeCosecRow(i)" />
        </div>
        <v-row dense>
          <v-col cols="12" md="6">
            <div class="ip-drop-wrap">
              <v-text-field v-model="c.name" :label="`Full Name${i === 0 ? ' *' : ''}`" variant="outlined" density="compact" hide-details>
                <template #append-inner>
                  <v-tooltip location="top" :open-delay="150">
                    <template #activator="{ props: tProps }">
                      <v-btn v-bind="tProps" icon="mdi-creation" size="x-small" variant="text" color="primary"
                        @click="requestLookup({ fieldGroup: 'cosec', index: i, defaultRole: cosecs[i]?.contactCd ? 'SECRETARY' : 'CONTACT' })" />
                    </template>
                    <span>{{ cosecs[i]?.contactCd ? 'Lookup secretary from chat' : 'Pick contact from chat' }}</span>
                  </v-tooltip>
                </template>
              </v-text-field>
              <div v-if="isDragging" class="ip-drop-overlay" @dragover.prevent
                @drop.prevent="handleFieldDrop($event, (v) => c.name = v)" />
            </div>
          </v-col>
          <v-col cols="12" md="3">
            <v-text-field v-model="c.licenseNo" label="License / Membership No" variant="outlined" density="compact" hide-details />
          </v-col>
          <v-col cols="12" md="3">
            <v-text-field v-model="c.ssmCertNo" label="SSM Practising Cert No" variant="outlined" density="compact" hide-details />
          </v-col>
          <!-- Section 236(3) 은 비서별로 별도 발행 — 모든 cosec 에 NRIC/Firm/Tel/Email/Fax 필요. -->
          <template v-if="docFlags.hasSection236_3">
            <v-col cols="12" md="6">
              <v-text-field v-model="c.nricNo" label="NRIC / Passport No" variant="outlined" density="compact" hide-details />
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field v-model="c.firm" label="Firm" variant="outlined" density="compact" hide-details />
            </v-col>
            <v-col cols="12" md="4">
              <v-text-field v-model="c.tel" label="Tel" variant="outlined" density="compact" hide-details />
            </v-col>
            <v-col cols="12" md="4">
              <v-text-field v-model="c.email" label="Email" variant="outlined" density="compact" hide-details />
            </v-col>
            <v-col cols="12" md="4">
              <v-text-field v-model="c.fax" label="Fax" variant="outlined" density="compact" hide-details />
            </v-col>
          </template>
        </v-row>
      </div>
      <v-btn variant="tonal" size="small" prepend-icon="mdi-plus" class="mt-2" @click="addCosecRow">Add Co-Sec</v-btn>

      <!-- ─── Co-Sec Common / Firm Info — 모든 비서에 공통 적용 ─── -->
      <div v-if="docFlags.hasSection236_3 || docFlags.hasPettyCash || docFlags.hasMembersWrConstitution" class="ip-cosec-common mt-3">
        <div class="ip-cosec-common-label">
          <v-icon size="14" color="grey-darken-1" class="mr-1">mdi-office-building-outline</v-icon>
          <span>Firm / Common Info</span>
        </div>
        <v-row dense>
          <v-col v-if="docFlags.hasSection236_3" cols="12" md="6">
            <v-text-field
              v-model="declarationLocalText"
              :placeholder="declarationDatePlaceholder"
              :label="isFieldSkipped('declarationDate') ? 'Declaration Date (Section 236(3))' : 'Declaration Date (Section 236(3)) *'"
              variant="outlined" density="compact" hide-details="auto"
              :class="{ 'ip-field-skipped': isFieldSkipped('declarationDate') }"
              @blur="declarationOnBlur($event.target.value)"
            >
              <template v-if="!isFieldSkipped('declarationDate')" #prepend-inner>
                <v-tooltip location="top" :open-delay="150">
                  <template #activator="{ props: tipProps }">
                    <span v-bind="tipProps" class="ip-skip-btn"
                      @mousedown.prevent
                      @click.stop="toggleFieldSkip('declarationDate', true)">
                      <v-icon size="14" color="grey-lighten-1">mdi-close</v-icon>
                    </span>
                  </template>
                  Skip — fill before lodging
                </v-tooltip>
              </template>
              <template #append-inner>
                <a v-if="isFieldSkipped('declarationDate')" class="ip-restore-link"
                  @click.stop="toggleFieldSkip('declarationDate', false)">Restore</a>
                <v-menu v-else v-model="declarationDateMenu" :close-on-content-click="false" location="bottom">
                  <template #activator="{ props: menuProps }">
                    <v-icon v-bind="menuProps" color="success" size="20" style="cursor:pointer">mdi-calendar</v-icon>
                  </template>
                  <v-date-picker
                    v-bind="declarationDatePickerProps"
                    v-model="declarationDatePickerValue"
                    @update:model-value="declarationDateMenu = false"
                  />
                </v-menu>
              </template>
            </v-text-field>
          </v-col>
          <template v-if="docFlags.hasPettyCash || docFlags.hasMembersWrConstitution">
            <v-col cols="12" md="6">
              <v-text-field
                v-model="cosecFirmName"
                label="Co-Sec Firm Name (for Petty Cash / Members WR)"
                variant="outlined" density="compact" hide-details
                placeholder="Enter firm name or select via AI"
              >
                <template #append-inner>
                  <v-tooltip location="top" :open-delay="150">
                    <template #activator="{ props: tProps }">
                      <v-btn v-bind="tProps" icon="mdi-creation" size="x-small" variant="text" color="primary"
                        @click="requestLookup({ fieldGroup: 'cosecFirm', defaultRole: 'CONTACT' })" />
                    </template>
                    <span>Select Co-Sec firm from contacts</span>
                  </v-tooltip>
                </template>
              </v-text-field>
            </v-col>
            <v-col cols="12" md="6">
              <div class="ip-drop-wrap">
                <v-text-field v-model="cosecFirmAddress" label="Co-Sec Firm Address" variant="outlined" density="compact" hide-details>
                  <template v-if="entityAddresses.length > 0" #append-inner>
                    <v-tooltip location="top" :open-delay="150">
                      <template #activator="{ props: tProps }">
                        <v-btn v-bind="tProps" icon="mdi-creation" size="x-small" variant="text" color="primary"
                          @click="emit('request-address-lookup', {
                            fieldGroup: 'cosecFirmAddress',
                            index: 0,
                            addresses: entityAddresses,
                            subjectLabel: 'Co-Sec Firm Address',
                          })" />
                      </template>
                      <span>Company address history ({{ entityAddresses.length }} options)</span>
                    </v-tooltip>
                  </template>
                </v-text-field>
                <div v-if="isDragging" class="ip-drop-overlay" @dragover.prevent
                  @drop.prevent="handleFieldDrop($event, (v) => cosecFirmAddress = v)" />
              </div>
            </v-col>
          </template>
        </v-row>
      </div>
    </div>

    <!-- ─── Allottees (Shares) ─── -->
    <div v-if="docFlags.hasFirstBoardMeeting || docFlags.hasPettyCash" class="ip-section ip-sec--amber">
      <div class="ip-section-header">
        <v-icon size="16" class="mr-1">mdi-chart-pie</v-icon>Allottees (Share Allocation)
        <span class="ip-chip-applies">Applies to:</span>
        <v-chip v-for="d in docsForSection('allottee').slice(0, MAX_DOC_CHIPS)" :key="d.tmpId" size="x-small" variant="tonal" color="primary" class="mx-1">
          {{ docLabel(d) }}
        </v-chip>
        <v-tooltip v-if="docsForSection('allottee').length > MAX_DOC_CHIPS" location="bottom" :open-delay="100">
          <template #activator="{ props: tProps }">
            <span v-bind="tProps" class="ip-more-chip">+{{ docsForSection('allottee').length - MAX_DOC_CHIPS }}</span>
          </template>
          <div class="ip-more-tooltip">
            <div v-for="d in docsForSection('allottee').slice(MAX_DOC_CHIPS)" :key="d.tmpId">{{ docLabel(d) }}</div>
          </div>
        </v-tooltip>
      </div>
      <v-row v-for="(a, i) in allottees" :key="`al-${i}`" dense align="center">
        <v-col cols="12" md="7">
          <div class="ip-drop-wrap">
            <v-text-field v-model="a.name" :label="`Allottee ${i + 1} Name`" variant="outlined" density="compact" hide-details>
              <template #append-inner>
                <v-tooltip location="top" :open-delay="150">
                  <template #activator="{ props: tProps }">
                    <v-btn v-bind="tProps" icon="mdi-creation" size="x-small" variant="text" color="primary"
                      @click="requestLookup({ fieldGroup: 'allottee', index: i, defaultRole: null })" />
                  </template>
                  <span>Lookup person from chat</span>
                </v-tooltip>
              </template>
            </v-text-field>
            <div v-if="isDragging" class="ip-drop-overlay" @dragover.prevent
              @drop.prevent="handleFieldDrop($event, (v) => a.name = v)" />
          </div>
        </v-col>
        <v-col cols="12" md="4">
          <v-text-field v-model.number="a.shares" label="Shares" type="number" variant="outlined" density="compact" hide-details />
        </v-col>
        <v-col cols="12" md="1" class="d-flex justify-end">
          <v-btn icon="mdi-close" size="x-small" variant="text" color="error" @click="removeAllotteeRow(i)" />
        </v-col>
      </v-row>
      <v-btn variant="tonal" size="small" prepend-icon="mdi-plus" class="mt-2 mb-3" @click="addAllotteeRow">Add Allottee</v-btn>
      <v-row dense>
        <v-col cols="12" md="3">
          <v-text-field
            v-model="totalShares"
            label="Total Shares"
            variant="outlined" density="compact" hide-details
            readonly persistent-hint hint="Auto = Σ allottee shares"
          />
        </v-col>
        <v-col cols="12" md="3">
          <v-text-field
            v-model="sharePrice"
            label="Share Price (RM)"
            type="number" step="0.01"
            variant="outlined" density="compact" hide-details
          />
        </v-col>
        <v-col cols="12" md="3">
          <v-text-field
            v-model="totalCashAmount"
            label="Total Cash (RM)"
            variant="outlined" density="compact" hide-details
            readonly persistent-hint hint="Auto = Total × Price"
          />
        </v-col>
        <v-col cols="12" md="3">
          <v-text-field
            v-model="totalCashAmountWords"
            label="Amount in Words"
            variant="outlined" density="compact" hide-details
            readonly
          />
        </v-col>
        <!-- Petty Cash 의 Letter Date — Auditor/Tax Agent/LHDN/Indemnity 와 ref 공유 (한 곳 수정 시 동기화) -->
        <v-col v-if="docFlags.hasPettyCash" cols="12" md="6">
          <v-text-field
            v-model="letterLocalText"
            :placeholder="letterDatePlaceholder"
            label="Letter Date (Petty Cash)"
            variant="outlined" density="compact" hide-details="auto"
            hint="Shared across letter docs"
            persistent-hint
            :class="{ 'ip-field-skipped': isFieldSkipped('letterDate') }"
            @blur="letterOnBlur($event.target.value)"
          >
            <template v-if="!isFieldSkipped('letterDate')" #prepend-inner>
              <v-tooltip location="top" :open-delay="150">
                <template #activator="{ props: tipProps }">
                  <span v-bind="tipProps" class="ip-skip-btn"
                    @mousedown.prevent @click.stop="toggleFieldSkip('letterDate', true)">
                    <v-icon size="14" color="grey-lighten-1">mdi-close</v-icon>
                  </span>
                </template>
                Skip — fill before lodging
              </v-tooltip>
            </template>
            <template #append-inner>
              <a v-if="isFieldSkipped('letterDate')" class="ip-restore-link"
                @click.stop="toggleFieldSkip('letterDate', false)">Restore</a>
              <v-menu v-else v-model="letterMenuPettyCash" :close-on-content-click="false" location="bottom">
                <template #activator="{ props: menuProps }">
                  <v-icon v-bind="menuProps" color="success" size="20" style="cursor:pointer">mdi-calendar</v-icon>
                </template>
                <v-date-picker
                  v-bind="letterDatePickerProps"
                  v-model="letterDatePickerValue"
                  @update:model-value="letterMenuPettyCash = false"
                />
              </v-menu>
            </template>
          </v-text-field>
        </v-col>
        <v-col v-if="docFlags.hasFirstBoardMeeting" cols="12" md="6">
          <v-text-field
            v-model="resolutionLocalText"
            :placeholder="resolutionDatePlaceholder"
            label="Resolution Date (First Board / DCRs)"
            variant="outlined" density="compact" hide-details="auto"
            hint="Shared across resolution docs"
            persistent-hint
            :class="{ 'ip-field-skipped': isFieldSkipped('resolutionDate') }"
            @blur="resolutionOnBlur($event.target.value)"
          >
            <template v-if="!isFieldSkipped('resolutionDate')" #prepend-inner>
              <v-tooltip location="top" :open-delay="150">
                <template #activator="{ props: tipProps }">
                  <span v-bind="tipProps" class="ip-skip-btn"
                    @mousedown.prevent @click.stop="toggleFieldSkip('resolutionDate', true)">
                    <v-icon size="14" color="grey-lighten-1">mdi-close</v-icon>
                  </span>
                </template>
                Skip — fill before lodging
              </v-tooltip>
            </template>
            <template #append-inner>
              <a v-if="isFieldSkipped('resolutionDate')" class="ip-restore-link"
                @click.stop="toggleFieldSkip('resolutionDate', false)">Restore</a>
              <v-menu v-else v-model="resolutionMenuFirstBoard" :close-on-content-click="false" location="bottom">
                <template #activator="{ props: menuProps }">
                  <v-icon v-bind="menuProps" color="success" size="20" style="cursor:pointer">mdi-calendar</v-icon>
                </template>
                <v-date-picker
                  v-bind="resolutionDatePickerProps"
                  v-model="resolutionDatePickerValue"
                  @update:model-value="resolutionMenuFirstBoard = false"
                />
              </v-menu>
            </template>
          </v-text-field>
        </v-col>
      </v-row>
    </div>

    <!-- ─── Auditor ─── -->
    <div v-if="docFlags.hasAuditorLetter || docFlags.hasFirstBoardMeeting" class="ip-section ip-sec--teal">
      <div class="ip-section-header">
        <v-icon size="16" class="mr-1">mdi-shield-check</v-icon>Auditor
        <span class="ip-chip-applies">Applies to:</span>
        <v-chip v-for="d in docsForSection('auditor').slice(0, MAX_DOC_CHIPS)" :key="d.tmpId" size="x-small" variant="tonal" color="primary" class="mx-1">
          {{ docLabel(d) }}
        </v-chip>
        <v-tooltip v-if="docsForSection('auditor').length > MAX_DOC_CHIPS" location="bottom" :open-delay="100">
          <template #activator="{ props: tProps }">
            <span v-bind="tProps" class="ip-more-chip">+{{ docsForSection('auditor').length - MAX_DOC_CHIPS }}</span>
          </template>
          <div class="ip-more-tooltip">
            <div v-for="d in docsForSection('auditor').slice(MAX_DOC_CHIPS)" :key="d.tmpId">{{ docLabel(d) }}</div>
          </div>
        </v-tooltip>
      </div>
      <v-row dense>
        <v-col cols="12">
          <div class="ip-drop-wrap">
            <v-text-field v-model="auditorName" label="Auditor Firm Name" variant="outlined" density="compact" hide-details>
              <template #append-inner>
                <v-tooltip location="top" :open-delay="150">
                  <template #activator="{ props: tProps }">
                    <v-btn v-bind="tProps" icon="mdi-creation" size="x-small" variant="text" color="primary"
                      @click="requestLookup({ fieldGroup: 'auditor', defaultRole: null })" />
                  </template>
                  <span>Lookup auditor from chat</span>
                </v-tooltip>
              </template>
            </v-text-field>
            <div v-if="isDragging" class="ip-drop-overlay" @dragover.prevent
              @drop.prevent="handleFieldDrop($event, (v) => auditorName = v)" />
          </div>
        </v-col>
        <v-col cols="12">
          <div class="ip-drop-wrap">
            <v-textarea v-model="auditorAddress" label="Auditor Address" rows="2" auto-grow variant="outlined" density="compact" hide-details>
              <template #append-inner>
                <v-tooltip location="top" :open-delay="150">
                  <template #activator="{ props: tProps }">
                    <v-btn v-bind="tProps" icon="mdi-creation" size="x-small" variant="text" color="primary"
                      @click="requestLookup({ fieldGroup: 'auditor', defaultRole: null })" />
                  </template>
                  <span>Lookup auditor from chat</span>
                </v-tooltip>
              </template>
            </v-textarea>
            <div v-if="isDragging" class="ip-drop-overlay" @dragover.prevent
              @drop.prevent="handleFieldDrop($event, (v) => auditorAddress = v)" />
            <div v-if="auditorAddresses.length >= 1" class="ip-addr-suggest">
              <v-chip
                v-for="(addr, ai) in auditorAddresses"
                :key="`aud-addr-${ai}`"
                size="x-small"
                variant="tonal"
                :color="auditorAddressSelectedIdx === ai ? 'primary' : 'default'"
                @click="() => { auditorAddress = addr.value; auditorAddressSelectedIdx = ai; }"
              >
                {{ addressSuggestionLabel(addr, ai, auditorAddresses) }}
              </v-chip>
            </div>
          </div>
        </v-col>
        <v-col v-if="docFlags.hasAuditorLetter" cols="12" md="6">
          <v-text-field
            v-model="letterLocalText"
            :placeholder="letterDatePlaceholder"
            label="Letter Date (Auditor)"
            variant="outlined" density="compact" hide-details="auto"
            hint="Shared across letter docs"
            persistent-hint
            :class="{ 'ip-field-skipped': isFieldSkipped('letterDate') }"
            @blur="letterOnBlur($event.target.value)"
          >
            <template v-if="!isFieldSkipped('letterDate')" #prepend-inner>
              <v-tooltip location="top" :open-delay="150">
                <template #activator="{ props: tipProps }">
                  <span v-bind="tipProps" class="ip-skip-btn"
                    @mousedown.prevent @click.stop="toggleFieldSkip('letterDate', true)">
                    <v-icon size="14" color="grey-lighten-1">mdi-close</v-icon>
                  </span>
                </template>
                Skip — fill before lodging
              </v-tooltip>
            </template>
            <template #append-inner>
              <a v-if="isFieldSkipped('letterDate')" class="ip-restore-link"
                @click.stop="toggleFieldSkip('letterDate', false)">Restore</a>
              <v-menu v-else v-model="letterMenuAuditor" :close-on-content-click="false" location="bottom">
                <template #activator="{ props: menuProps }">
                  <v-icon v-bind="menuProps" color="success" size="20" style="cursor:pointer">mdi-calendar</v-icon>
                </template>
                <v-date-picker
                  v-bind="letterDatePickerProps"
                  v-model="letterDatePickerValue"
                  @update:model-value="letterMenuAuditor = false"
                />
              </v-menu>
            </template>
          </v-text-field>
        </v-col>
      </v-row>
    </div>

    <!-- ─── Tax Agent ─── -->
    <div v-if="docFlags.hasTaxAgentLetter || docFlags.hasLhdnLetter || docFlags.hasFirstBoardMeeting" class="ip-section ip-sec--rose">
      <div class="ip-section-header">
        <v-icon size="16" class="mr-1">mdi-calculator</v-icon>Tax Agent
        <span class="ip-chip-applies">Applies to:</span>
        <v-chip v-for="d in docsForSection('taxAgent').slice(0, MAX_DOC_CHIPS)" :key="d.tmpId" size="x-small" variant="tonal" color="primary" class="mx-1">
          {{ docLabel(d) }}
        </v-chip>
        <v-tooltip v-if="docsForSection('taxAgent').length > MAX_DOC_CHIPS" location="bottom" :open-delay="100">
          <template #activator="{ props: tProps }">
            <span v-bind="tProps" class="ip-more-chip">+{{ docsForSection('taxAgent').length - MAX_DOC_CHIPS }}</span>
          </template>
          <div class="ip-more-tooltip">
            <div v-for="d in docsForSection('taxAgent').slice(MAX_DOC_CHIPS)" :key="d.tmpId">{{ docLabel(d) }}</div>
          </div>
        </v-tooltip>
      </div>
      <v-row dense>
        <v-col cols="12">
          <div class="ip-drop-wrap">
            <v-text-field v-model="taxAgentName" label="Tax Agent Firm Name" variant="outlined" density="compact" hide-details>
              <template #append-inner>
                <v-tooltip location="top" :open-delay="150">
                  <template #activator="{ props: tProps }">
                    <v-btn v-bind="tProps" icon="mdi-creation" size="x-small" variant="text" color="primary"
                      @click="requestLookup({ fieldGroup: 'taxAgent', defaultRole: null })" />
                  </template>
                  <span>Lookup tax agent from chat</span>
                </v-tooltip>
              </template>
            </v-text-field>
            <div v-if="isDragging" class="ip-drop-overlay" @dragover.prevent
              @drop.prevent="handleFieldDrop($event, (v) => taxAgentName = v)" />
          </div>
        </v-col>
        <v-col cols="12">
          <div class="ip-drop-wrap">
            <v-textarea v-model="taxAgentAddress" label="Tax Agent Address" rows="2" auto-grow variant="outlined" density="compact" hide-details>
              <template #append-inner>
                <v-tooltip location="top" :open-delay="150">
                  <template #activator="{ props: tProps }">
                    <v-btn v-bind="tProps" icon="mdi-creation" size="x-small" variant="text" color="primary"
                      @click="requestLookup({ fieldGroup: 'taxAgent', defaultRole: null })" />
                  </template>
                  <span>Lookup tax agent from chat</span>
                </v-tooltip>
              </template>
            </v-textarea>
            <div v-if="isDragging" class="ip-drop-overlay" @dragover.prevent
              @drop.prevent="handleFieldDrop($event, (v) => taxAgentAddress = v)" />
          </div>
          <div v-if="taxAgentAddresses.length >= 1" class="ip-addr-suggest">
            <v-chip
              v-for="(addr, ai) in taxAgentAddresses"
              :key="`ta-addr-${ai}`"
              size="x-small"
              variant="tonal"
              :color="taxAgentAddressSelectedIdx === ai ? 'primary' : 'default'"
              @click="() => { taxAgentAddress = addr.value; taxAgentAddressSelectedIdx = ai; }"
            >
              {{ addressSuggestionLabel(addr, ai, taxAgentAddresses) }}
            </v-chip>
          </div>
        </v-col>
        <v-col v-if="docFlags.hasTaxAgentLetter" cols="12" md="6">
          <v-text-field
            v-model="letterLocalText"
            :placeholder="letterDatePlaceholder"
            label="Letter Date (Tax Agent)"
            variant="outlined" density="compact" hide-details="auto"
            hint="Shared across letter docs"
            persistent-hint
            :class="{ 'ip-field-skipped': isFieldSkipped('letterDate') }"
            @blur="letterOnBlur($event.target.value)"
          >
            <template v-if="!isFieldSkipped('letterDate')" #prepend-inner>
              <v-tooltip location="top" :open-delay="150">
                <template #activator="{ props: tipProps }">
                  <span v-bind="tipProps" class="ip-skip-btn"
                    @mousedown.prevent @click.stop="toggleFieldSkip('letterDate', true)">
                    <v-icon size="14" color="grey-lighten-1">mdi-close</v-icon>
                  </span>
                </template>
                Skip — fill before lodging
              </v-tooltip>
            </template>
            <template #append-inner>
              <a v-if="isFieldSkipped('letterDate')" class="ip-restore-link"
                @click.stop="toggleFieldSkip('letterDate', false)">Restore</a>
              <v-menu v-else v-model="letterMenuTaxAgent" :close-on-content-click="false" location="bottom">
                <template #activator="{ props: menuProps }">
                  <v-icon v-bind="menuProps" color="success" size="20" style="cursor:pointer">mdi-calendar</v-icon>
                </template>
                <v-date-picker
                  v-bind="letterDatePickerProps"
                  v-model="letterDatePickerValue"
                  @update:model-value="letterMenuTaxAgent = false"
                />
              </v-menu>
            </template>
          </v-text-field>
        </v-col>
      </v-row>
    </div>

    <!-- ─── LHDN 전용 추가 필드 ─── -->
    <div v-if="docFlags.hasLhdnLetter" class="ip-section ip-sec--orange">
      <div class="ip-section-header">
        <v-icon size="16" class="mr-1">mdi-bank</v-icon>LHDN Letter Details
        <span class="ip-chip-applies">Applies to:</span>
        <v-chip v-for="d in docsForSection('lhdn').slice(0, MAX_DOC_CHIPS)" :key="d.tmpId" size="x-small" variant="tonal" color="primary" class="mx-1">
          {{ docLabel(d) }}
        </v-chip>
        <v-tooltip v-if="docsForSection('lhdn').length > MAX_DOC_CHIPS" location="bottom" :open-delay="100">
          <template #activator="{ props: tProps }">
            <span v-bind="tProps" class="ip-more-chip">+{{ docsForSection('lhdn').length - MAX_DOC_CHIPS }}</span>
          </template>
          <div class="ip-more-tooltip">
            <div v-for="d in docsForSection('lhdn').slice(MAX_DOC_CHIPS)" :key="d.tmpId">{{ docLabel(d) }}</div>
          </div>
        </v-tooltip>
      </div>
      <v-row dense>
        <v-col cols="12" md="6">
          <v-text-field v-model="lhdnRefNo" label="LHDN Ref No (Rujukan Tuan)" variant="outlined" density="compact" hide-details />
        </v-col>
        <v-col cols="12" md="6">
          <v-text-field v-model="taxAgentRegistrationBracket" label="Tax Agent Reg No (e.g. (AF1234))" variant="outlined" density="compact" hide-details />
        </v-col>
        <v-col cols="12" md="6">
          <v-text-field v-model="taxAgentEmail" label="Tax Agent Email" variant="outlined" density="compact" hide-details />
        </v-col>
        <v-col cols="12" md="6">
          <v-text-field v-model="taxAgentApprovalNo" label="Tax Agent Approval No (Nombor Kelulusan)" variant="outlined" density="compact" hide-details />
        </v-col>
        <v-col cols="12" md="6">
          <v-text-field v-model="taxAgentOfficerName" label="Tax Agent Officer Name (Ejen Cukai)" variant="outlined" density="compact" hide-details />
        </v-col>
        <v-col cols="12" md="6">
          <v-text-field v-model="directorNameForLhdn" label="Signing Director Name (Pengarah)" variant="outlined" density="compact" hide-details>
            <template #append-inner>
              <v-tooltip location="top" :open-delay="150">
                <template #activator="{ props: tProps }">
                  <v-btn v-bind="tProps" icon="mdi-creation" size="x-small" variant="text" color="primary"
                    @click="requestLookup({ fieldGroup: 'lhdnDirector', defaultRole: 'DIRECTOR' })" />
                </template>
                <span>Select signing director from chat</span>
              </v-tooltip>
            </template>
          </v-text-field>
        </v-col>
        <v-col cols="12" md="6">
          <v-text-field
            v-model="letterLocalText"
            :placeholder="letterDatePlaceholder"
            label="Letter Date (LHDN / Tarikh)"
            variant="outlined" density="compact" hide-details="auto"
            hint="Shared across letter docs"
            persistent-hint
            :class="{ 'ip-field-skipped': isFieldSkipped('letterDate') }"
            @blur="letterOnBlur($event.target.value)"
          >
            <template v-if="!isFieldSkipped('letterDate')" #prepend-inner>
              <v-tooltip location="top" :open-delay="150">
                <template #activator="{ props: tipProps }">
                  <span v-bind="tipProps" class="ip-skip-btn"
                    @mousedown.prevent @click.stop="toggleFieldSkip('letterDate', true)">
                    <v-icon size="14" color="grey-lighten-1">mdi-close</v-icon>
                  </span>
                </template>
                Skip — fill before lodging
              </v-tooltip>
            </template>
            <template #append-inner>
              <a v-if="isFieldSkipped('letterDate')" class="ip-restore-link"
                @click.stop="toggleFieldSkip('letterDate', false)">Restore</a>
              <v-menu v-else v-model="letterMenuLhdn" :close-on-content-click="false" location="bottom">
                <template #activator="{ props: menuProps }">
                  <v-icon v-bind="menuProps" color="success" size="20" style="cursor:pointer">mdi-calendar</v-icon>
                </template>
                <v-date-picker
                  v-bind="letterDatePickerProps"
                  v-model="letterDatePickerValue"
                  @update:model-value="letterMenuLhdn = false"
                />
              </v-menu>
            </template>
          </v-text-field>
        </v-col>
      </v-row>
    </div>

    <!-- ─── Constitution (선택) ─── -->
    <div v-if="docFlags.hasDcrConstitution || docFlags.hasMembersWrConstitution || docFlags.hasConstitutionAnnexure" class="ip-section ip-sec--indigo">
      <div class="ip-section-header">
        <v-icon size="16" class="mr-1">mdi-file-document-edit</v-icon>Constitution
        <span class="ip-chip-applies">Applies to:</span>
        <v-chip v-for="d in docsForSection('constitution').slice(0, MAX_DOC_CHIPS)" :key="d.tmpId" size="x-small" variant="tonal" color="primary" class="mx-1">
          {{ docLabel(d) }}
        </v-chip>
        <v-tooltip v-if="docsForSection('constitution').length > MAX_DOC_CHIPS" location="bottom" :open-delay="100">
          <template #activator="{ props: tProps }">
            <span v-bind="tProps" class="ip-more-chip">+{{ docsForSection('constitution').length - MAX_DOC_CHIPS }}</span>
          </template>
          <div class="ip-more-tooltip">
            <div v-for="d in docsForSection('constitution').slice(MAX_DOC_CHIPS)" :key="d.tmpId">{{ docLabel(d) }}</div>
          </div>
        </v-tooltip>
      </div>
      <v-checkbox v-model="hasConstitution" label="Has Constitution" density="compact" hide-details class="mb-2" />
      <template v-if="hasConstitution">
        <v-row dense>
          <v-col cols="12">
            <v-autocomplete
              v-model="businessObject1"
              :items="msicOptions"
              item-title="title"
              item-value="desc"
              label="Business Object 1 (MSIC)"
              placeholder="Search MSIC code or description"
              variant="outlined"
              density="compact"
              hide-details
              clearable
              auto-select-first
            />
          </v-col>
          <v-col cols="12">
            <v-autocomplete
              v-model="businessObject2"
              :items="msicOptions"
              item-title="title"
              item-value="desc"
              label="Business Object 2 (MSIC)"
              placeholder="Search MSIC code or description"
              variant="outlined"
              density="compact"
              hide-details
              clearable
              auto-select-first
            />
          </v-col>
          <v-col cols="12">
            <v-autocomplete
              v-model="businessObject3"
              :items="msicOptions"
              item-title="title"
              item-value="desc"
              label="Business Object 3 (MSIC)"
              placeholder="Search MSIC code or description"
              variant="outlined"
              density="compact"
              hide-details
              clearable
              auto-select-first
            />
          </v-col>
          <v-col cols="12" md="4">
            <v-combobox
              v-model="constitutionClauseNoModel"
              :items="constitutionClauseOptions"
              label="Constitution Clause No"
              placeholder="Select or type"
              variant="outlined"
              density="compact"
              hide-details="auto"
              clearable
              :hint="
                entityArticleNoOptions.length > 0
                  ? `${entityArticleNoOptions.length} option(s) from entity profile`
                  : 'No options from entity profile — type manually'
              "
              persistent-hint
            />
          </v-col>
          <!-- Constitution / Annexure 의 Resolution Date — DCR / Common Seal 과 ref 공유 -->
          <v-col v-if="docFlags.hasDcrConstitution || docFlags.hasConstitutionAnnexure" cols="12" md="4">
            <v-text-field
              v-model="resolutionLocalText"
              :placeholder="resolutionDatePlaceholder"
              label="Resolution Date (DCR / Annexure)"
              variant="outlined" density="compact" hide-details="auto"
              hint="Shared across resolution docs"
              persistent-hint
              :class="{ 'ip-field-skipped': isFieldSkipped('resolutionDate') }"
              @blur="resolutionOnBlur($event.target.value)"
            >
              <template v-if="!isFieldSkipped('resolutionDate')" #prepend-inner>
                <v-tooltip location="top" :open-delay="150">
                  <template #activator="{ props: tipProps }">
                    <span v-bind="tipProps" class="ip-skip-btn"
                      @mousedown.prevent @click.stop="toggleFieldSkip('resolutionDate', true)">
                      <v-icon size="14" color="grey-lighten-1">mdi-close</v-icon>
                    </span>
                  </template>
                  Skip — fill before lodging
                </v-tooltip>
              </template>
              <template #append-inner>
                <a v-if="isFieldSkipped('resolutionDate')" class="ip-restore-link"
                  @click.stop="toggleFieldSkip('resolutionDate', false)">Restore</a>
                <v-menu v-else v-model="resolutionMenuConstitution" :close-on-content-click="false" location="bottom">
                  <template #activator="{ props: menuProps }">
                    <v-icon v-bind="menuProps" color="success" size="20" style="cursor:pointer">mdi-calendar</v-icon>
                  </template>
                  <v-date-picker
                    v-bind="resolutionDatePickerProps"
                    v-model="resolutionDatePickerValue"
                    @update:model-value="resolutionMenuConstitution = false"
                  />
                </v-menu>
              </template>
            </v-text-field>
          </v-col>
          <v-col v-if="docFlags.hasMembersWrConstitution" cols="12" md="4">
            <v-text-field
              v-model="circulationLocalText"
              :placeholder="circulationDatePlaceholder"
              label="Circulation Date (Members WR)"
              variant="outlined" density="compact" hide-details="auto"
              :class="{ 'ip-field-skipped': isFieldSkipped('circulationDate') }"
              @blur="circulationOnBlur($event.target.value)"
            >
              <template v-if="!isFieldSkipped('circulationDate')" #prepend-inner>
                <v-tooltip location="top" :open-delay="150">
                  <template #activator="{ props: tipProps }">
                    <span v-bind="tipProps" class="ip-skip-btn"
                      @mousedown.prevent @click.stop="toggleFieldSkip('circulationDate', true)">
                      <v-icon size="14" color="grey-lighten-1">mdi-close</v-icon>
                    </span>
                  </template>
                  Skip — fill before lodging
                </v-tooltip>
              </template>
              <template #append-inner>
                <a v-if="isFieldSkipped('circulationDate')" class="ip-restore-link"
                  @click.stop="toggleFieldSkip('circulationDate', false)">Restore</a>
                <v-menu v-else v-model="circulationDateMenu" :close-on-content-click="false" location="bottom">
                  <template #activator="{ props: menuProps }">
                    <v-icon v-bind="menuProps" color="success" size="20" style="cursor:pointer">mdi-calendar</v-icon>
                  </template>
                  <v-date-picker
                    v-bind="circulationDatePickerProps"
                    v-model="circulationDatePickerValue"
                    @update:model-value="circulationDateMenu = false"
                  />
                </v-menu>
              </template>
            </v-text-field>
          </v-col>
          <v-col v-if="docFlags.hasMembersWrConstitution" cols="12" md="4">
            <v-text-field
              v-model="deadlineLocalText"
              :placeholder="deadlineDatePlaceholder"
              label="Deadline Date (Members Written Resolution)"
              variant="outlined" density="compact" hide-details="auto"
              :class="{ 'ip-field-skipped': isFieldSkipped('deadlineDate') }"
              @blur="deadlineOnBlur($event.target.value)"
            >
              <template v-if="!isFieldSkipped('deadlineDate')" #prepend-inner>
                <v-tooltip location="top" :open-delay="150">
                  <template #activator="{ props: tipProps }">
                    <span v-bind="tipProps" class="ip-skip-btn"
                      @mousedown.prevent @click.stop="toggleFieldSkip('deadlineDate', true)">
                      <v-icon size="14" color="grey-lighten-1">mdi-close</v-icon>
                    </span>
                  </template>
                  Skip — fill before lodging
                </v-tooltip>
              </template>
              <template #append-inner>
                <a v-if="isFieldSkipped('deadlineDate')" class="ip-restore-link"
                  @click.stop="toggleFieldSkip('deadlineDate', false)">Restore</a>
                <v-menu v-else v-model="deadlineDateMenu" :close-on-content-click="false" location="bottom">
                  <template #activator="{ props: menuProps }">
                    <v-icon v-bind="menuProps" color="success" size="20" style="cursor:pointer">mdi-calendar</v-icon>
                  </template>
                  <v-date-picker
                    v-bind="deadlineDatePickerProps"
                    v-model="deadlineDatePickerValue"
                    @update:model-value="deadlineDateMenu = false"
                  />
                </v-menu>
              </template>
            </v-text-field>
          </v-col>
        </v-row>
      </template>
    </div>

    <!-- ─── Lodger (Constitution Annexure A) ─── -->
    <div v-if="docFlags.hasConstitutionAnnexure" class="ip-section ip-sec--cyan">
      <div class="ip-section-header">
        <v-icon size="16" class="mr-1">mdi-account-arrow-right</v-icon>Lodger (Constitution Annexure A)
        <span class="ip-chip-applies">Applies to:</span>
        <v-chip v-for="d in docsForSection('lodger').slice(0, MAX_DOC_CHIPS)" :key="d.tmpId" size="x-small" variant="tonal" color="primary" class="mx-1">
          {{ docLabel(d) }}
        </v-chip>
        <v-tooltip v-if="docsForSection('lodger').length > MAX_DOC_CHIPS" location="bottom" :open-delay="100">
          <template #activator="{ props: tProps }">
            <span v-bind="tProps" class="ip-more-chip">+{{ docsForSection('lodger').length - MAX_DOC_CHIPS }}</span>
          </template>
          <div class="ip-more-tooltip">
            <div v-for="d in docsForSection('lodger').slice(MAX_DOC_CHIPS)" :key="d.tmpId">{{ docLabel(d) }}</div>
          </div>
        </v-tooltip>
      </div>
      <v-row dense>
        <v-col cols="12" md="6">
          <div class="ip-drop-wrap">
            <v-text-field v-model="lodgerName" label="Lodger Name" variant="outlined" density="compact" hide-details>
              <template #append-inner>
                <v-tooltip location="top" :open-delay="150">
                  <template #activator="{ props: tProps }">
                    <v-btn v-bind="tProps" icon="mdi-creation" size="x-small" variant="text" color="primary"
                      @click="requestLookup({ fieldGroup: 'lodger', defaultRole: null })" />
                  </template>
                  <span>Lookup lodger from chat</span>
                </v-tooltip>
              </template>
            </v-text-field>
            <div v-if="isDragging" class="ip-drop-overlay" @dragover.prevent
              @drop.prevent="handleFieldDrop($event, (v) => lodgerName = v)" />
          </div>
        </v-col>
        <v-col cols="12" md="6">
          <v-text-field v-model="lodgerNric" label="NRIC No" variant="outlined" density="compact" hide-details />
        </v-col>
        <v-col cols="12">
          <v-textarea v-model="lodgerAddress" label="Address" rows="2" auto-grow variant="outlined" density="compact" hide-details />
        </v-col>
        <v-col cols="12" md="6">
          <v-text-field v-model="lodgerPhone" label="Phone" variant="outlined" density="compact" hide-details />
        </v-col>
        <v-col cols="12" md="6">
          <v-text-field v-model="lodgerEmail" label="Email" variant="outlined" density="compact" hide-details />
        </v-col>
      </v-row>
    </div>

    <!-- ─── Common Seal (선택) ─── -->
    <div v-if="docFlags.hasDcrCommonSeal" class="ip-section ip-sec--amber">
      <div class="ip-section-header">
        <v-icon size="16" class="mr-1">mdi-seal</v-icon>Common Seal
        <span class="ip-chip-applies">Applies to:</span>
        <v-chip v-for="d in docsForSection('commonSeal').slice(0, MAX_DOC_CHIPS)" :key="d.tmpId" size="x-small" variant="tonal" color="primary" class="mx-1">
          {{ docLabel(d) }}
        </v-chip>
        <v-tooltip v-if="docsForSection('commonSeal').length > MAX_DOC_CHIPS" location="bottom" :open-delay="100">
          <template #activator="{ props: tProps }">
            <span v-bind="tProps" class="ip-more-chip">+{{ docsForSection('commonSeal').length - MAX_DOC_CHIPS }}</span>
          </template>
          <div class="ip-more-tooltip">
            <div v-for="d in docsForSection('commonSeal').slice(MAX_DOC_CHIPS)" :key="d.tmpId">{{ docLabel(d) }}</div>
          </div>
        </v-tooltip>
      </div>
      <v-checkbox v-model="hasCommonSeal" label="Adopt Common Seal" density="compact" hide-details class="mb-2" />
      <v-row v-if="hasCommonSeal" dense>
        <v-col cols="12" md="6">
          <v-combobox
            v-model="constitutionClauseNoModel"
            :items="constitutionClauseOptions"
            label="Constitution Clause No (Common Seal)"
            placeholder="Select or type"
            variant="outlined" density="compact" hide-details="auto" clearable
          />
        </v-col>
        <v-col cols="12" md="6">
          <v-text-field
            v-model="resolutionLocalText"
            :placeholder="resolutionDatePlaceholder"
            label="Resolution Date (DCR Common Seal)"
            variant="outlined" density="compact" hide-details="auto"
            hint="Shared across resolution docs"
            persistent-hint
            :class="{ 'ip-field-skipped': isFieldSkipped('resolutionDate') }"
            @blur="resolutionOnBlur($event.target.value)"
          >
            <template v-if="!isFieldSkipped('resolutionDate')" #prepend-inner>
              <v-tooltip location="top" :open-delay="150">
                <template #activator="{ props: tipProps }">
                  <span v-bind="tipProps" class="ip-skip-btn"
                    @mousedown.prevent @click.stop="toggleFieldSkip('resolutionDate', true)">
                    <v-icon size="14" color="grey-lighten-1">mdi-close</v-icon>
                  </span>
                </template>
                Skip — fill before lodging
              </v-tooltip>
            </template>
            <template #append-inner>
              <a v-if="isFieldSkipped('resolutionDate')" class="ip-restore-link"
                @click.stop="toggleFieldSkip('resolutionDate', false)">Restore</a>
              <v-menu v-else v-model="resolutionMenuCommonSeal" :close-on-content-click="false" location="bottom">
                <template #activator="{ props: menuProps }">
                  <v-icon v-bind="menuProps" color="success" size="20" style="cursor:pointer">mdi-calendar</v-icon>
                </template>
                <v-date-picker
                  v-bind="resolutionDatePickerProps"
                  v-model="resolutionDatePickerValue"
                  @update:model-value="resolutionMenuCommonSeal = false"
                />
              </v-menu>
            </template>
          </v-text-field>
        </v-col>
      </v-row>
    </div>

    <!-- ─── Nominee Director Indemnity (조건부) ─── -->
    <div v-if="docFlags.hasLetterOfIndemnity" class="ip-section ip-sec--purple">
      <div class="ip-section-header">
        <v-icon size="16" class="mr-1">mdi-shield-account</v-icon>Letter of Indemnity (Nominee Director)
        <span class="ip-chip-applies">Applies to:</span>
        <v-chip v-for="d in docsForSection('indemnity').slice(0, MAX_DOC_CHIPS)" :key="d.tmpId" size="x-small" variant="tonal" color="primary" class="mx-1">
          {{ docLabel(d) }}
        </v-chip>
        <v-tooltip v-if="docsForSection('indemnity').length > MAX_DOC_CHIPS" location="bottom" :open-delay="100">
          <template #activator="{ props: tProps }">
            <span v-bind="tProps" class="ip-more-chip">+{{ docsForSection('indemnity').length - MAX_DOC_CHIPS }}</span>
          </template>
          <div class="ip-more-tooltip">
            <div v-for="d in docsForSection('indemnity').slice(MAX_DOC_CHIPS)" :key="d.tmpId">{{ docLabel(d) }}</div>
          </div>
        </v-tooltip>
      </div>
      <v-checkbox
        v-model="hasNomineeDirector"
        label="Has Nominee Director (issue Letter of Indemnity)"
        density="compact" hide-details class="mb-2"
      />
      <v-row v-if="hasNomineeDirector" dense>
        <v-col cols="12">
          <div class="ip-drop-wrap">
            <v-text-field v-model="nomineeDirectorName" label="Nominee Director Name" variant="outlined" density="compact" hide-details>
              <template #append-inner>
                <v-tooltip location="top" :open-delay="150">
                  <template #activator="{ props: tProps }">
                    <v-btn v-bind="tProps" icon="mdi-creation" size="x-small" variant="text" color="primary"
                      @click="requestLookup({ fieldGroup: 'nomineeDirector', defaultRole: 'DIRECTOR' })" />
                  </template>
                  <span>Lookup nominee director from chat</span>
                </v-tooltip>
              </template>
            </v-text-field>
            <div v-if="isDragging" class="ip-drop-overlay" @dragover.prevent
              @drop.prevent="handleFieldDrop($event, (v) => nomineeDirectorName = v)" />
          </div>
        </v-col>
        <v-col cols="12">
          <v-textarea v-model="nomineeDirectorAddress" label="Nominee Director Address" rows="2" auto-grow variant="outlined" density="compact" hide-details />
        </v-col>
        <v-col cols="12" md="6">
          <v-text-field
            v-model="letterLocalText"
            :placeholder="letterDatePlaceholder"
            label="Letter Date (Indemnity)"
            variant="outlined" density="compact" hide-details="auto"
            hint="Shared across letter docs"
            persistent-hint
            :class="{ 'ip-field-skipped': isFieldSkipped('letterDate') }"
            @blur="letterOnBlur($event.target.value)"
          >
            <template v-if="!isFieldSkipped('letterDate')" #prepend-inner>
              <v-tooltip location="top" :open-delay="150">
                <template #activator="{ props: tipProps }">
                  <span v-bind="tipProps" class="ip-skip-btn"
                    @mousedown.prevent @click.stop="toggleFieldSkip('letterDate', true)">
                    <v-icon size="14" color="grey-lighten-1">mdi-close</v-icon>
                  </span>
                </template>
                Skip — fill before lodging
              </v-tooltip>
            </template>
            <template #append-inner>
              <a v-if="isFieldSkipped('letterDate')" class="ip-restore-link"
                @click.stop="toggleFieldSkip('letterDate', false)">Restore</a>
              <v-menu v-else v-model="letterMenuIndemnity" :close-on-content-click="false" location="bottom">
                <template #activator="{ props: menuProps }">
                  <v-icon v-bind="menuProps" color="success" size="20" style="cursor:pointer">mdi-calendar</v-icon>
                </template>
                <v-date-picker
                  v-bind="letterDatePickerProps"
                  v-model="letterDatePickerValue"
                  @update:model-value="letterMenuIndemnity = false"
                />
              </v-menu>
            </template>
          </v-text-field>
        </v-col>
      </v-row>
    </div>

    <!-- ─── 최종 문서 리스트 (Will generate / Skipped) ─── -->
    <div class="ip-section ip-sec--slate">
      <div class="ip-section-header">
        <v-icon size="16" class="mr-1">mdi-file-document-multiple-outline</v-icon>
        Documents ({{ selection.documents.length - skippedDocsCount }} of {{ selection.documents.length }})
        <v-chip v-if="skippedDocsCount > 0" size="x-small" color="warning" variant="tonal" class="ml-2 ip-chip-skipped">
          <v-icon start size="12" color="black">mdi-cancel</v-icon>{{ skippedDocsCount }} skipped
        </v-chip>
      </div>
      <div
        v-for="(doc, idx) in selection.documents"
        :key="(doc as any).docCode || `doc-${idx}`"
        class="ip-doc-item"
        :class="{ 'ip-doc-skipped': isDocSkipped((doc as any).templateName || '').skipped }"
      >
        <span class="ip-doc-num">{{ idx + 1 }}</span>
        <v-icon size="14" color="primary" class="mr-2">mdi-file-document-outline</v-icon>
        <span class="ip-doc-name">{{ docLabel(doc) }}</span>
        <v-spacer />
        <v-chip
          v-if="isDocSkipped((doc as any).templateName || '').skipped"
          size="x-small" color="warning" variant="tonal"
          text-color="black" class="ip-chip-skipped"
        >
          <v-icon start size="12" color="black">mdi-cancel</v-icon>
          Skipped — {{ isDocSkipped((doc as any).templateName || '').reason }}
        </v-chip>
        <v-chip v-else size="x-small" color="success" variant="tonal">
          <v-icon start size="12">mdi-check</v-icon>Will generate
        </v-chip>
      </div>
    </div>

    </template>
    <!-- (formReady 종료) -->

    <!-- Footer -->
    <div class="ip-footer">
      <v-btn variant="outlined" :disabled="creating" @click="emit('back')">Back</v-btn>
      <v-spacer />
      <v-tooltip :disabled="!(editMode && !isDirty)" location="top" text="No changes to update">
        <template #activator="{ props: tProps }">
          <span v-bind="tProps">
            <v-btn
              color="primary"
              variant="flat"
              :prepend-icon="creating ? '' : (editMode ? 'mdi-content-save' : 'mdi-check')"
              :loading="creating"
              :disabled="creating || (editMode && !isDirty)"
              @click="handleCreateTask"
            >
              {{ creating ? (editMode ? 'Updating…' : 'Creating…') : (editMode ? 'Update Task' : 'Create Task') }}
            </v-btn>
          </span>
        </template>
      </v-tooltip>
    </div>

    <!-- Progress bar — Create Task 실행 중 표시 -->
    <div v-if="creating || progress" class="ip-progress-wrap">
      <v-progress-linear
        v-if="creating"
        indeterminate color="primary" height="4" rounded
      />
      <div
        v-if="progress"
        class="ip-progress-text"
        :class="{ 'ip-progress-error': progress.startsWith('Error:') }"
      >
        {{ progress }}
      </div>
    </div>
  </div>
</template>

<style scoped>
.ip-panel { display: flex; flex-direction: column; gap: 12px; padding: 8px; }
.ip-header {
  display: flex; align-items: flex-start; justify-content: space-between;
  padding: 4px 0;
}
.ip-title { font-size: 18px; font-weight: 700; color: #3c4650; }
.ip-subtitle { font-size: 12px; color: #6B7280; margin-top: 2px; }
.ip-section {
  background: #fff; border: 1px solid #E4E9EF; border-radius: 8px;
  padding: 12px 14px;
}
.ip-sec--blue   { background: linear-gradient(150deg, #EEF4FF 0%, #ffffff 55%); border-color: #C7D9FF; }
.ip-sec--green  { background: linear-gradient(150deg, #EDFAF4 0%, #ffffff 55%); border-color: #B8EDD5; }
.ip-sec--purple { background: linear-gradient(150deg, #F4EEFF 0%, #ffffff 55%); border-color: #D9C7FF; }
.ip-sec--amber  { background: linear-gradient(150deg, #FFFBEE 0%, #ffffff 55%); border-color: #F5E49A; }
.ip-sec--teal   { background: linear-gradient(150deg, #EDFBF8 0%, #ffffff 55%); border-color: #B2EAE0; }
.ip-sec--rose   { background: linear-gradient(150deg, #FFF0F4 0%, #ffffff 55%); border-color: #FFCCD9; }
.ip-sec--orange { background: linear-gradient(150deg, #FFF5EE 0%, #ffffff 55%); border-color: #FFD4B2; }
.ip-sec--indigo { background: linear-gradient(150deg, #EEF0FF 0%, #ffffff 55%); border-color: #C4CAFF; }
.ip-sec--cyan   { background: linear-gradient(150deg, #EDF9FF 0%, #ffffff 55%); border-color: #B2E4F5; }
.ip-sec--slate  { background: linear-gradient(150deg, #F5F7FA 0%, #ffffff 55%); border-color: #DDE3EC; }
.ip-section-header {
  display: flex; align-items: center;
  font-size: 13px; font-weight: 600; color: #3c4650; margin-bottom: 8px;
}
.ip-readonly-row {
  display: flex; align-items: center; justify-content: space-between; gap: 12px;
  padding: 8px 12px; background: #F5F8FA; border-radius: 6px;
}
.ip-readonly-text { font-size: 14px; color: #3c4650; font-weight: 500; }
.ip-entity-card {
  background: #f0f5ff; border: 1px solid #D6E4FF; border-radius: 8px;
  padding: 12px 14px;
}
.ip-entity-header { display: flex; align-items: center; gap: 12px; }
.ip-entity-info { flex: 1; }
.ip-entity-name { font-size: 15px; font-weight: 700; color: #3c4650; }
.ip-entity-meta { font-size: 12px; color: #4B5563; margin-top: 2px; }
.ip-footer {
  display: flex; align-items: center; gap: 8px;
  padding: 12px 0; border-top: 1px solid #E4E9EF;
}
.ip-progress-wrap { padding: 4px 8px 8px; }
.ip-progress-text {
  font-size: 12px; color: #6B7280; text-align: center; margin-top: 6px;
}
.ip-progress-error { color: #d32f2f; font-weight: 600; }
.ip-chip-applies { font-size: 11px; color: #6B7280; margin-left: 8px; font-weight: 500; }
.ip-more-chip {
  font-size: 11px;
  color: #6B7280;
  cursor: default;
  text-decoration: underline dotted;
  text-underline-offset: 2px;
}
.ip-more-tooltip {
  font-size: 12px;
  line-height: 1.8;
  white-space: nowrap;
}
.ip-subrow {
  border: 1px solid #E4E9EF; border-radius: 6px; padding: 8px 10px;
  margin-bottom: 10px; background: #FAFBFC;
}
.ip-subrow-header {
  display: flex; align-items: center;
  font-size: 12px; color: #3c4650; margin-bottom: 6px;
}
.ip-drop-wrap { position: relative; }
.ip-drop-overlay {
  position: absolute; inset: 0;
  border: 2px dashed #1B84FF; border-radius: 6px;
  background: rgba(27, 132, 255, 0.06);
  pointer-events: all; cursor: copy;
}
.ip-addr-suggest {
  display: flex; flex-wrap: wrap; gap: 4px;
  margin-top: 4px; padding: 0 4px;
}
.ip-empty-guide {
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  padding: 60px 24px; text-align: center;
  background: #FAFBFC; border: 1px dashed #D6E4FF; border-radius: 8px;
}
.ip-empty-title { font-size: 14px; font-weight: 600; color: #3c4650; margin-bottom: 4px; }
.ip-empty-sub { font-size: 12px; color: #6B7280; }
.ip-loading {
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  padding: 60px 24px; text-align: center;
  background: #F8FAFE; border: 1px solid #D6E4FF; border-radius: 8px;
}
.ip-loading-text { font-size: 13px; color: #3c4650; margin-top: 12px; }
.ip-doc-item {
  display: flex; align-items: center; gap: 6px;
  padding: 8px 10px; border-bottom: 1px solid #F0F2F5;
}
.ip-doc-item:last-child { border-bottom: none; }
.ip-doc-item.ip-doc-skipped { opacity: 0.55; }
.ip-doc-num {
  font-size: 11px; color: #6B7280; font-weight: 600;
  min-width: 18px; text-align: right; margin-right: 4px;
}
.ip-doc-name { font-size: 13px; color: #3c4650; font-weight: 500; }
.ip-skip-btn {
  display: inline-flex; align-items: center; justify-content: center;
  width: 20px; height: 20px; border-radius: 50%;
  cursor: pointer; transition: background 0.15s ease; margin-right: 2px;
}
.ip-skip-btn:hover { background: rgba(244, 67, 54, 0.12); }
.ip-skip-btn:hover :deep(.v-icon) { color: #f44336 !important; }
.ip-field-skipped :deep(.v-field) { background-color: #f5f5f5; }
.ip-field-skipped :deep(.v-label) { text-decoration: line-through; opacity: 0.6; }
.ip-restore-link {
  font-size: 12px; color: #1976d2; cursor: pointer;
  text-decoration: underline; white-space: nowrap;
}
.ip-restore-link:hover { color: #0d47a1; }
.ip-cosec-common {
  border-top: 1px dashed #e0e0e0;
  padding-top: 12px;
}
.ip-cosec-common-label {
  font-size: 11px; font-weight: 600; color: #757575;
  text-transform: uppercase; letter-spacing: 0.5px;
  margin-bottom: 8px; display: flex; align-items: center;
}
/* Vuetify warning tonal chip 의 inner 색상 강제 — 노란 배경 위 검정 글씨로 가독성 확보 */
.ip-chip-skipped { color: #000 !important; }
.ip-chip-skipped :deep(.v-chip__content),
.ip-chip-skipped :deep(.v-icon) { color: #000 !important; }
</style>
